import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import {
    Scissors, Users, Calendar, CalendarDays, CalendarPlus, DollarSign, LogOut, Plus, Edit, Trash2,
    TrendingUp, UserPlus, MoreVertical, Star, Phone, Mail, Briefcase,
    BarChart3, Activity, Settings, Check, X, ArrowLeft, Banknote,
    Smartphone, Building2, CreditCard, Package, Warehouse, ShoppingCart, Receipt, Download, Clock, MessageCircle, UtensilsCrossed, Search, Menu, Save, AlertCircle, CheckCircle2
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { formatAppointmentDate, formatAppointmentDateShort, getLocalDateString, getTimeSlotDisplayItems } from '../utils/dateUtils';
import { getVisiblePageNumbers } from '../utils/pagination';
import { toWhatsAppNumber } from '../utils/phoneUtils';
import './AdminDashboard.css';
import Inventory from './Inventory';
import Compras from './Compras/Compras';
import Debe from './Debe/Debe';
import ScheduleAdmin from './ScheduleAdmin';
import MultiAppointmentModal from './MultiAppointmentModal/MultiAppointmentModal';

// Mapeo URL slug ↔ nombre interno de pestaña
const SECTION_TO_TAB = {
    barberos:   'barbers',
    citas:      'appointments',
    datos:      'analytics',
    servicios:  'services',
    inventario: 'inventory',
    horarios:   'horarios',
};
const TAB_TO_SECTION = Object.fromEntries(
    Object.entries(SECTION_TO_TAB).map(([k, v]) => [v, k])
);

const APPOINTMENTS_PAGE_SIZE = 20;

const DEFAULT_WHATSAPP_REVIEW_MESSAGE =
    'Gracias por venir y disfrutar de nuestros servicios en Genuinos barber shop te invitamos a que nos califiques en google maps y nos dejes tus opiniones: https://maps.app.goo.gl/srhiQ6W1jcYEpcVS7?g_st=aw';

const AdminDashboard = () => {
    const { section } = useParams();
    const [activeTab, setActiveTab] = useState(() => SECTION_TO_TAB[section] || 'barbers');
    const [activeSubTab, setActiveSubTab] = useState('analytics'); // Para sub-tabs de Datos y Compras
    const [barbers, setBarbers] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [appointmentsPagination, setAppointmentsPagination] = useState({
        total: 0,
        limit: APPOINTMENTS_PAGE_SIZE,
        offset: 0,
        has_more: false,
        currentPage: 1
    });
    // Filtros de la vista de citas: por día de la semana y búsqueda de cliente
    const [appointmentDayFilter, setAppointmentDayFilter] = useState('all'); // 'all' | 0..6 (0=Dom, 1=Lun, ...)
    const [appointmentSearchQuery, setAppointmentSearchQuery] = useState('');
    /** Sincronizado con la paginación de citas para refresh/polling sin saltar a la página 1. */
    const appointmentsCurrentPageRef = useRef(1);
    const lastReliableAdminAppointmentsTotalRef = useRef(0);
    const [stats, setStats] = useState({
        total_revenue: 0,
        total_appointments: 0,
        active_barbers: 0,
        total_barbers: 0,
        average_service: 0
    });
    const [revenueData, setRevenueData] = useState([]);
    const [paymentMethodsData, setPaymentMethodsData] = useState([]);
    const [analyticsAppointments, setAnalyticsAppointments] = useState([]);
    const [analyticsBarberFilter, setAnalyticsBarberFilter] = useState('ALL');
    const [dailyDebt, setDailyDebt] = useState({ total_debt: 0, purchases_count: 0 });
    const [loading, setLoading] = useState(true);
    const [loadingFilters, setLoadingFilters] = useState(false);
    const [dateFilters, setDateFilters] = useState(() => {
        const today = getLocalDateString();
        return { startDate: today, endDate: today };
    });
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingBarber, setEditingBarber] = useState(null);
    const [error, setError] = useState(null);

    // Estados para gestión de servicios
    const [services, setServices] = useState([]);
    const [serviceModules, setServiceModules] = useState([]);
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [showServiceModuleModal, setShowServiceModuleModal] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [editingModule, setEditingModule] = useState(null);
    const [serviceFormData, setServiceFormData] = useState({
        name: '',
        description: '',
        price: '',
        duration_minutes: '',
        module_id: '',
        active: true
    });
    const [moduleFormData, setModuleFormData] = useState({
        name: '',
        display_name: '',
        description: '',
        image_url: '',
        active: true,
        display_order: 0
    });
    const navigate = useNavigate();

    // Función para cambiar de pestaña actualizando la URL
    const goToTab = useCallback((tab, subTab = null) => {
        setActiveTab(tab);
        if (subTab) setActiveSubTab(subTab);
        navigate(`/admin/${TAB_TO_SECTION[tab] || 'barberos'}`, { replace: true });
    }, [navigate]);

    // Sincronizar URL → pestaña cuando el usuario navega con el botón atrás/adelante
    useEffect(() => {
        const tab = SECTION_TO_TAB[section] || 'barbers';
        setActiveTab(tab);
    }, [section]);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        phone: '',
        specialty: '',
        bio: '',
        image_url: '',
        experience_years: 0,
        available: true
    });
    const [barberImageFile, setBarberImageFile] = useState(null); // archivo seleccionado para subir (solo en edición)
    const [barberImageInputKey, setBarberImageInputKey] = useState(0); // para resetear el input file al eliminar imagen

    // Estado para registro manual de cortes
    const [showManualCutModal, setShowManualCutModal] = useState(false);
    const [selectedBarberForCut, setSelectedBarberForCut] = useState(null);
    const [manualCutForm, setManualCutForm] = useState({
        service_name: '',
        client_name: '',
        price: '',
        payment_method: 'efectivo',
        amount: '',
        notes: ''
    });

    // Estado para gestión de módulos
    const [showModuleModal, setShowModuleModal] = useState(false);
    const [selectedBarberForModules, setSelectedBarberForModules] = useState(null);
    const [allModules, setAllModules] = useState([]);
    const [barberModules, setBarberModules] = useState([]);
    const [loadingModules, setLoadingModules] = useState(false);

    // Estado para horarios de descanso por barbero
    const [showBreakHoursModal, setShowBreakHoursModal] = useState(false);
    const [selectedBarberForBreaks, setSelectedBarberForBreaks] = useState(null);
    const [barberBreakHours, setBarberBreakHours] = useState([]);
    const [loadingBreakHours, setLoadingBreakHours] = useState(false);

    // Estado para horario extendido (hasta qué hora puede agendar cada día; solo en perfil del barbero)
    const [showExtendedHoursModal, setShowExtendedHoursModal] = useState(false);
    const [selectedBarberForExtendedHours, setSelectedBarberForExtendedHours] = useState(null);
    const [barberExtendedHours, setBarberExtendedHours] = useState([]); // [{ day_of_week, end_time }, ...]
    const [loadingExtendedHours, setLoadingExtendedHours] = useState(false);

    // Estado para agendar citas desde admin
    const [showBookAppointmentModal, setShowBookAppointmentModal] = useState(false);
    const [editingAppointmentId, setEditingAppointmentId] = useState(null); // si está set, el modal está en modo edición
    const [appointmentStep, setAppointmentStep] = useState('barber'); // 'barber', 'service', 'datetime'
    const [selectedBarberForAppointment, setSelectedBarberForAppointment] = useState(null);
    const [barberServices, setBarberServices] = useState([]);
    const [selectedService, setSelectedService] = useState(null);
    const [appointmentForm, setAppointmentForm] = useState({
        client_name: '',
        client_phone: '',
        client_email: '',
        appointment_date: '',
        appointment_time: '',
        duration_minutes: 30,
        price: '',
        notes: ''
    });
    const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
    const [loadingServices, setLoadingServices] = useState(false);
    const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);

    // Productos de inventario en edición de cita (solo admin)
    const [appointmentInventoryLines, setAppointmentInventoryLines] = useState([]);
    const [inventoryCatalog, setInventoryCatalog] = useState([]);
    const [inventoryCategories, setInventoryCategories] = useState([]);
    const [inventoryCategoryFilter, setInventoryCategoryFilter] = useState('');
    const [loadingAppointmentInventory, setLoadingAppointmentInventory] = useState(false);

    // Estados para el modal de pago
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [appointmentToComplete, setAppointmentToComplete] = useState(null);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
    const [servicePrice, setServicePrice] = useState(0);

    // Estado para pagos pendientes
    const [pendingPayments, setPendingPayments] = useState([]);
    const [loadingPendingPayments, setLoadingPendingPayments] = useState(false);

    // Estado para modal de calificación
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewClientInfo, setReviewClientInfo] = useState({ phone: '', name: '' });

    // Menú móvil (hamburguesa): solo visible en vista celular
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Subsección Equipo de barberos: Barberos | Clientes
    const [barbersSubTab, setBarbersSubTab] = useState('barbers');
    const [clientsList, setClientsList] = useState([]);
    const [clientsTotal, setClientsTotal] = useState(0);
    const [clientsSearch, setClientsSearch] = useState('');
    const [clientsPage, setClientsPage] = useState(1);
    const CLIENTS_PAGE_SIZE = 20;
    const [loadingClients, setLoadingClients] = useState(false);
    const [showClientModal, setShowClientModal] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [showClientAppointmentsModal, setShowClientAppointmentsModal] = useState(false);
    const [selectedClientForAppointments, setSelectedClientForAppointments] = useState(null);
    const [showMultiAppointmentModal, setShowMultiAppointmentModal] = useState(false);
    const [selectedClientForBooking, setSelectedClientForBooking] = useState(null);
    const [clientAppointmentsList, setClientAppointmentsList] = useState([]);
    const [loadingClientAppointments, setLoadingClientAppointments] = useState(false);
    const [clientForm, setClientForm] = useState({ name: '', phone: '', email: '', password: '' });

    // Configuración del sitio (mensaje WhatsApp de reseña)
    const [whatsappReviewMessage, setWhatsappReviewMessage] = useState(DEFAULT_WHATSAPP_REVIEW_MESSAGE);
    const [settingsDraft, setSettingsDraft] = useState(DEFAULT_WHATSAPP_REVIEW_MESSAGE);
    const [loadingSettings, setLoadingSettings] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [settingsNotice, setSettingsNotice] = useState(null);

    const serviciosList = [
        'Corte Premium', 'Barba & Afeitado', 'Combo Completo',
        'Decoloración', 'Tratamiento Capilar', 'Diseño de Barba'
    ];

    const handleManualCutSubmit = async (e) => {
        e.preventDefault();
        if (!selectedBarberForCut) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/admin/appointments/manual`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    barber_id: selectedBarberForCut.id,
                    service_name: manualCutForm.service_name,
                    client_name: manualCutForm.client_name || 'Cliente Manual',
                    price: parseFloat(manualCutForm.price) || 0,
                    payment_method: manualCutForm.payment_method || 'efectivo',
                    amount: manualCutForm.amount !== '' ? parseFloat(manualCutForm.amount) : (parseFloat(manualCutForm.price) || 0),
                    notes: manualCutForm.notes,
                    appointment_date: getLocalDateString(),
                    appointment_time: new Date().toLocaleTimeString('es-CO', { hour12: false })
                })
            });

            if (response.ok) {
                alert('Corte registrado exitosamente');
                setShowManualCutModal(false);
                setManualCutForm({ service_name: '', client_name: '', price: '', payment_method: 'efectivo', amount: '', notes: '' });
                loadData(); // Recargar estadísticas
            } else {
                alert('Error al registrar el corte');
            }
        } catch (error) {
            console.error('Error recording cut:', error);
            alert('Error al conectar con el servidor');
        }
    };

    const loadSiteSettings = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) return;
        setLoadingSettings(true);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/settings`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                const msg = data.whatsapp_review_message || DEFAULT_WHATSAPP_REVIEW_MESSAGE;
                setWhatsappReviewMessage(msg);
                setSettingsDraft(msg);
            }
        } catch (err) {
            console.error('Error cargando configuración:', err);
        } finally {
            setLoadingSettings(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
        // Cargar solo datos iniciales (barberos y stats básicos)
        loadInitialData();
        loadSiteSettings();
    }, [loadSiteSettings]);

    const saveWhatsappReviewMessage = async () => {
        const token = localStorage.getItem('token');
        if (!token) return;
        const value = settingsDraft.trim();
        if (!value) {
            setSettingsNotice({ type: 'error', message: 'El mensaje no puede estar vacío' });
            return;
        }
        setSavingSettings(true);
        setSettingsNotice(null);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/settings/whatsapp-review-message`, {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ value }),
            });
            if (res.ok) {
                const data = await res.json();
                const saved = data.whatsapp_review_message || value;
                setWhatsappReviewMessage(saved);
                setSettingsDraft(saved);
                setSettingsNotice({ type: 'success', message: 'Mensaje guardado correctamente' });
            } else {
                const err = await res.json().catch(() => ({}));
                setSettingsNotice({ type: 'error', message: err.detail || 'Error al guardar' });
            }
        } catch {
            setSettingsNotice({ type: 'error', message: 'Error de conexión con el servidor' });
        } finally {
            setSavingSettings(false);
        }
    };

    const openWhatsAppReview = useCallback((phone) => {
        const cleanPhone = toWhatsAppNumber(phone);
        const message = encodeURIComponent(whatsappReviewMessage || DEFAULT_WHATSAPP_REVIEW_MESSAGE);
        window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
    }, [whatsappReviewMessage]);

    // Cargar datos según la pestaña activa (también cuando termina la carga inicial)
    useEffect(() => {
        if (loading) return;
        loadTabData(activeTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- loadTabData usa estado actual; evita loop
    }, [activeTab, loading]);

    // Cargar datos cuando cambia la sub-pestaña
    useEffect(() => {
        if (loading) return;
        if (activeSubTab === 'pending_payments') {
            loadPendingPayments();
        }
        if (activeTab === 'analytics' && activeSubTab === 'analytics') {
            loadTabData('analytics');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSubTab]);

    // Cargar clientes con paginación y búsqueda (orden: más recientes primero)
    const loadClients = useCallback(async (page = 1, searchTerm = '') => {
        const token = localStorage.getItem('token');
        if (!token) return;
        setLoadingClients(true);
        try {
            const offset = (page - 1) * CLIENTS_PAGE_SIZE;
            const params = new URLSearchParams({ limit: String(CLIENTS_PAGE_SIZE), offset: String(offset) });
            const term = (typeof searchTerm === 'string' ? searchTerm : '').trim();
            if (term) params.set('q', term);
            const res = await fetch(`${API_BASE_URL}/admin/clients?${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const json = await res.json();
                setClientsList(json.data || []);
                setClientsTotal(json.total ?? 0);
            } else {
                setClientsList([]);
                setClientsTotal(0);
            }
        } catch {
            setClientsList([]);
            setClientsTotal(0);
        } finally {
            setLoadingClients(false);
        }
    }, []);

    const loadClientAppointments = useCallback(async (clientId) => {
        const token = localStorage.getItem('token');
        if (!token || !clientId) return;
        setLoadingClientAppointments(true);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/clients/${clientId}/appointments`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setClientAppointmentsList(Array.isArray(data) ? data : []);
            } else {
                setClientAppointmentsList([]);
            }
        } catch {
            setClientAppointmentsList([]);
        } finally {
            setLoadingClientAppointments(false);
        }
    }, []);

    const openClientAppointmentsModal = (client) => {
        setSelectedClientForAppointments(client);
        setShowClientAppointmentsModal(true);
        loadClientAppointments(client.id);
    };

    // Valor de búsqueda con debounce (350ms) para no disparar la API en cada tecla
    const [clientsSearchDebounced, setClientsSearchDebounced] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setClientsSearchDebounced(clientsSearch), 350);
        return () => clearTimeout(t);
    }, [clientsSearch]);

    // Al cambiar búsqueda debounced, volver a página 1
    useEffect(() => {
        setClientsPage(1);
    }, [clientsSearchDebounced]);

    // Cargar clientes al estar en vista Clientes, al cambiar página o al cambiar búsqueda (debounced)
    useEffect(() => {
        if (activeTab === 'barbers' && barbersSubTab === 'clients') {
            loadClients(clientsPage, clientsSearchDebounced);
        }
    }, [activeTab, barbersSubTab, clientsPage, clientsSearchDebounced, loadClients]);

    useEffect(() => {
        appointmentsCurrentPageRef.current = appointmentsPagination.currentPage;
        if (typeof appointmentsPagination.total === 'number') {
            lastReliableAdminAppointmentsTotalRef.current = appointmentsPagination.total;
        }
    }, [appointmentsPagination.currentPage, appointmentsPagination.total]);

    // Refrescar citas manteniendo la página actual (polling cada 30s no debe volver a la página 1).
    const refreshAppointments = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) return;

        const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };

        const page = Math.max(1, appointmentsCurrentPageRef.current);
        const offset = (page - 1) * APPOINTMENTS_PAGE_SIZE;

        try {
            const appointmentsResponse = await fetch(
                `${API_BASE_URL}/admin/appointments?limit=${APPOINTMENTS_PAGE_SIZE}&offset=${offset}`,
                { headers }
            );
            if (appointmentsResponse.ok) {
                const appointmentsData = await appointmentsResponse.json();
                if (appointmentsData.appointments && Array.isArray(appointmentsData.appointments)) {
                    setAppointments(appointmentsData.appointments);
                    setAppointmentsPagination(prev => {
                        let total = prev.total;
                        if (typeof appointmentsData.total === 'number' && appointmentsData.total >= 0) {
                            lastReliableAdminAppointmentsTotalRef.current = appointmentsData.total;
                            total = appointmentsData.total;
                        } else if (lastReliableAdminAppointmentsTotalRef.current > 0) {
                            total = lastReliableAdminAppointmentsTotalRef.current;
                        }
                        const hasMore =
                            appointmentsData.has_more ??
                            offset + appointmentsData.appointments.length < total;
                        return {
                            ...prev,
                            total,
                            limit: APPOINTMENTS_PAGE_SIZE,
                            offset,
                            has_more: hasMore,
                            currentPage: page
                        };
                    });
                }
            } else {
                console.warn('refreshAppointments: respuesta HTTP', appointmentsResponse.status);
            }
        } catch (error) {
            console.error('Error refreshing appointments:', error);
        }
    }, []);

    // Polling automático para actualizar citas cada 30 segundos cuando la pestaña está activa
    useEffect(() => {
        if (activeTab !== 'appointments') return;

        // Primera carga inmediata al cambiar a la pestaña de citas
        refreshAppointments();

        // Configurar intervalo de actualización cada 30 segundos
        const intervalId = setInterval(() => {
            refreshAppointments();
        }, 30000); // 30 segundos

        // Limpiar intervalo cuando cambie de pestaña o se desmonte el componente
        return () => {
            clearInterval(intervalId);
        };
    }, [activeTab, refreshAppointments]); // Incluimos refreshAppointments en las dependencias

    const checkAuth = () => {
        const token = localStorage.getItem('token');
        const userRole = localStorage.getItem('userRole');

        if (!token || userRole !== 'admin') {
            console.log('Auth check failed:', { token: !!token, userRole });
            navigate('/login');
            return;
        }

        // Verificar que el token no esté expirado decodificándolo
        try {
            const tokenParts = token.split('.');
            if (tokenParts.length === 3) {
                const payload = JSON.parse(atob(tokenParts[1]));
                const exp = payload.exp;
                const now = Math.floor(Date.now() / 1000);

                if (exp && exp < now) {
                    console.error('Token expirado');
                    setError('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
                    setTimeout(() => handleLogout(), 2000);
                    return;
                }

                console.log('Token válido, rol:', payload.role);
                if (payload.role !== 'admin') {
                    console.error('Usuario no es admin, rol:', payload.role);
                    setError('No tienes permisos de administrador. Por favor, inicia sesión con una cuenta de administrador.');
                    setTimeout(() => handleLogout(), 2000);
                    return;
                }
            }
        } catch (e) {
            console.error('Error al decodificar token:', e);
        }
    };

    // Función helper para manejar errores 401
    const handle401Error = (message) => {
        console.error(message);
        setError('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
        setTimeout(() => handleLogout(), 2000);
    };

    // Cargar solo datos iniciales (barberos y stats básicos)
    const loadInitialData = async (filters = dateFilters) => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('token');

            if (!token) {
                console.error('No hay token de autenticación');
                setError('No hay sesión activa. Por favor, inicia sesión nuevamente.');
                setTimeout(() => handleLogout(), 2000);
                return;
            }

            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            // Stats con el mismo rango de fechas que Análisis (si hay filtros activos)
            const statsParams = new URLSearchParams();
            if (filters.startDate) statsParams.append('start_date', filters.startDate);
            if (filters.endDate) statsParams.append('end_date', filters.endDate);
            const statsQs = statsParams.toString();
            const statsUrl = statsQs
                ? `${API_BASE_URL}/admin/dashboard/stats?${statsQs}`
                : `${API_BASE_URL}/admin/dashboard/stats`;

            const [barbersResponse, statsResponse] = await Promise.all([
                fetch(`${API_BASE_URL}/admin/barbers`, { headers }),
                fetch(statsUrl, { headers })
            ]);

            if (barbersResponse.status === 401 || statsResponse.status === 401) {
                handle401Error('Token inválido o expirado');
                return;
            }

            if (barbersResponse.ok) {
                const barbersData = await barbersResponse.json();
                setBarbers(barbersData || []);
            }

            if (statsResponse.ok) {
                const statsData = await statsResponse.json();
                setStats({
                    total_revenue: statsData.total_revenue || 0,
                    total_appointments: statsData.total_appointments || 0,
                    active_barbers: statsData.active_barbers || 0,
                    total_barbers: statsData.total_barbers || 0,
                    average_service: statsData.average_service || 0
                });
            }

        } catch (error) {
            console.error('Error loading initial data:', error);
            if (error.message && error.message.includes('Failed to fetch')) {
                setError('Error de conexión con el servidor. Verifica que el backend esté ejecutándose.');
            } else {
                setError('Error al cargar los datos. Por favor, intenta recargar la página.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Cargar datos según la pestaña activa
    const loadTabData = async (tab, filters = dateFilters) => {
        const token = localStorage.getItem('token');
        if (!token) return;

        const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };

        try {
            switch (tab) {
                case 'barbers':
                    // Si ya tenemos barberos, no recargar
                    if (barbers.length > 0) return;

                    const barbersResponse = await fetch(`${API_BASE_URL}/admin/barbers`, { headers });
                    if (barbersResponse.ok) {
                        const barbersData = await barbersResponse.json();
                        setBarbers(barbersData || []);
                    }
                    break;

                case 'appointments':
                    // Las citas se cargan automáticamente mediante el polling en el useEffect
                    // No necesitamos cargarlas aquí para evitar duplicación
                    break;

                case 'analytics':
                case 'purchases':
                    // Siempre recargar (respeta filtros de fecha; evita quedar con datos de otro rango)
                    const params = new URLSearchParams();
                    if (filters.startDate) params.append('start_date', filters.startDate);
                    if (filters.endDate) params.append('end_date', filters.endDate);
                    const queryString = params.toString();
                    const urlSuffix = queryString ? `?${queryString}` : '';

                    const [revenueResponse, paymentMethodsResponse, dailyDebtResponse, analyticsAptsResponse] = await Promise.all([
                        fetch(`${API_BASE_URL}/admin/analytics/revenue${urlSuffix}`, { headers }),
                        fetch(`${API_BASE_URL}/admin/analytics/payment-methods${urlSuffix}`, { headers }),
                        fetch(`${API_BASE_URL}/admin/analytics/daily-debt`, { headers }),
                        fetch(`${API_BASE_URL}/admin/analytics/appointments${urlSuffix}`, { headers })
                    ]);

                    if (revenueResponse.ok) {
                        const revenue = await revenueResponse.json();
                        setRevenueData(revenue);
                    }

                    if (paymentMethodsResponse.ok) {
                        const paymentMethods = await paymentMethodsResponse.json();
                        setPaymentMethodsData(paymentMethods);
                    }

                    if (dailyDebtResponse.ok) {
                        const debtData = await dailyDebtResponse.json();
                        setDailyDebt(debtData);
                    }

                    if (analyticsAptsResponse.ok) {
                        const aptsData = await analyticsAptsResponse.json();
                        setAnalyticsAppointments(aptsData.appointments || []);
                    } else {
                        setAnalyticsAppointments([]);
                    }
                    break;

                case 'services':
                    // Cargar servicios y módulos solo cuando se necesite
                    if (services.length > 0 && serviceModules.length > 0) return;

                    const [servicesResponse, modulesResponse] = await Promise.all([
                        fetch(`${API_BASE_URL}/admin/services`, { headers }),
                        fetch(`${API_BASE_URL}/admin/service-modules`, { headers })
                    ]);

                    if (servicesResponse.ok) {
                        const servicesData = await servicesResponse.json();
                        setServices(servicesData);
                    }

                    if (modulesResponse.ok) {
                        const modulesData = await modulesResponse.json();
                        setServiceModules(modulesData);
                    }
                    break;

                case 'pending_payments':
                    loadPendingPayments();
                    break;

                default:
                    break;
            }
        } catch (error) {
            console.error(`Error loading tab data for ${tab}:`, error);
        }
    };

    // Cargar una página de citas (20 por página). La API devuelve solo esa página.
    const loadAppointmentsPage = async (page) => {
        const token = localStorage.getItem('token');
        if (!token || page < 1) return;

        const totalBasis =
            appointmentsPagination.total > 0
                ? appointmentsPagination.total
                : lastReliableAdminAppointmentsTotalRef.current;
        const totalPages = Math.max(1, Math.ceil(totalBasis / APPOINTMENTS_PAGE_SIZE));
        if (page > totalPages) return;

        const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
        const offset = (page - 1) * APPOINTMENTS_PAGE_SIZE;

        try {
            const appointmentsResponse = await fetch(
                `${API_BASE_URL}/admin/appointments?limit=${APPOINTMENTS_PAGE_SIZE}&offset=${offset}`,
                { headers }
            );

            if (appointmentsResponse.ok) {
                const appointmentsData = await appointmentsResponse.json();
                if (appointmentsData.appointments && Array.isArray(appointmentsData.appointments)) {
                    setAppointments(appointmentsData.appointments);
                    if (typeof appointmentsData.total === 'number' && appointmentsData.total >= 0) {
                        lastReliableAdminAppointmentsTotalRef.current = appointmentsData.total;
                    }
                    setAppointmentsPagination(prev => {
                        const total =
                            typeof appointmentsData.total === 'number'
                                ? appointmentsData.total
                                : lastReliableAdminAppointmentsTotalRef.current > 0
                                  ? lastReliableAdminAppointmentsTotalRef.current
                                  : prev.total;
                        return {
                            ...prev,
                            total,
                            offset,
                            has_more:
                                appointmentsData.has_more ??
                                offset + appointmentsData.appointments.length < total,
                            currentPage: page
                        };
                    });
                }
            } else {
                console.warn('loadAppointmentsPage: HTTP', appointmentsResponse.status);
            }
        } catch (error) {
            console.error('Error loading appointments page:', error);
        }
    };

    // Función para cargar pagos pendientes
    const loadPendingPayments = async () => {
        setLoadingPendingPayments(true);
        try {
            const token = localStorage.getItem('token');
            // Buscamos un rango amplio de citas para encontrar las pendientes
            // Idealmente el backend debería filtrar esto, pero lo haremos en frontend por ahora
            const response = await fetch(`${API_BASE_URL}/admin/appointments?limit=200`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                const allAppointments = data.appointments || (Array.isArray(data) ? data : []);

                // Filtrar las que tienen 'pago pendiente'
                const pending = allAppointments.filter(apt => {
                    let isPending = false;

                    // Caso 1: Propiedad de nivel superior
                    if (apt.payment_method === 'pago pendiente') {
                        isPending = true;
                    }

                    // Caso 2: Dentro de payment_data (JSONB)
                    if (!isPending && apt.payment_data) {
                        try {
                            let pData = apt.payment_data;
                            if (typeof pData === 'string') {
                                pData = JSON.parse(pData);
                            }

                            if (pData && pData.payment_method && pData.payment_method.toLowerCase() === 'pago pendiente') {
                                isPending = true;
                                if (!apt.service_price && pData.amount) {
                                    apt.service_price = pData.amount;
                                }
                            }
                        } catch (e) {
                            console.error('Error parsing payment_data', e);
                        }
                    }

                    return isPending;
                });

                setPendingPayments(pending);
            }
        } catch (error) {
            console.error('Error loading pending payments:', error);
        } finally {
            setLoadingPendingPayments(false);
        }
    };

    // Función loadData para recargar todo (usada en algunos casos)
    // filtersOverride permite recargar con un rango concreto (p. ej. al limpiar a "hoy")
    const loadData = async (filtersOverride = null) => {
        const filters =
            filtersOverride &&
            typeof filtersOverride === 'object' &&
            ('startDate' in filtersOverride || 'endDate' in filtersOverride)
                ? filtersOverride
                : dateFilters;
        await loadInitialData(filters);
        await loadTabData(activeTab, filters);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        navigate('/login');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            // Asegurar tipos que el backend espera (evita 422 por validación)
            let imageUrlToSave = formData.image_url?.trim() || null;
            if (editingBarber && barberImageFile) {
                const form = new FormData();
                form.append('file', barberImageFile);
                const uploadRes = await fetch(`${API_BASE_URL}/admin/barbers/${editingBarber.id}/upload-photo`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                    body: form
                });
                if (!uploadRes.ok) {
                    const errData = await uploadRes.json().catch(() => ({}));
                    throw new Error(errData.detail || 'Error al subir la imagen');
                }
                const uploadData = await uploadRes.json();
                const base = (API_BASE_URL || '').replace(/\/$/, '');
                imageUrlToSave = uploadData.image_url ? (uploadData.image_url.startsWith('http') ? uploadData.image_url : base + uploadData.image_url) : null;
            }

            const payload = {
                name: formData.name?.trim() || '',
                email: formData.email?.trim() || '',
                phone: formData.phone?.trim() || null,
                specialty: formData.specialty?.trim() || null,
                bio: formData.bio?.trim() || null,
                image_url: imageUrlToSave,
                experience_years: typeof formData.experience_years === 'number'
                    ? formData.experience_years
                    : parseInt(formData.experience_years, 10) || 0,
                available: Boolean(formData.available)
            };
            if (formData.password?.trim()) {
                payload.password = formData.password.trim();
            }

            let response;
            if (editingBarber) {
                response = await fetch(`${API_BASE_URL}/admin/barbers/${editingBarber.id}`, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify({ ...payload, role: 'barber' })
                });
            } else {
                response = await fetch(`${API_BASE_URL}/admin/barbers`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        ...payload,
                        password: formData.password?.trim() || '',
                        role: 'barber'
                    })
                });
            }

            if (response.ok) {
                setShowAddModal(false);
                setEditingBarber(null);
                setBarberImageFile(null);
                setFormData({
                    name: '', email: '', password: '', phone: '', specialty: '',
                    bio: '', image_url: '', experience_years: 0, available: true
                });
                loadData();
            } else {
                const errorData = await response.json().catch(() => ({}));
                let msg = 'Error al guardar barbero';
                const d = errorData.detail;
                if (Array.isArray(d)) {
                    msg = d.map(e => (e && (e.msg || e.message)) || JSON.stringify(e)).join('. ');
                } else if (d && typeof d === 'object') {
                    msg = d.msg || d.message || JSON.stringify(d);
                } else if (typeof d === 'string') {
                    msg = d;
                }
                alert(msg);
            }
        } catch (error) {
            console.error('Error:', error);
            alert(error?.message || 'Error al guardar barbero');
        }
    };

    const handleEdit = (barber) => {
        setEditingBarber(barber);
        setBarberImageFile(null);
        setFormData({
            name: barber.name || '',
            email: barber.email || '',
            password: '',
            phone: barber.phone || '',
            specialty: barber.specialty || '',
            bio: barber.bio || '',
            image_url: barber.image_url || '',
            experience_years: barber.experience_years || 0,
            available: barber.available !== undefined ? barber.available : true
        });
        setShowAddModal(true);
    };

    const handleDelete = async (barberId) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar este barbero?')) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/admin/barbers/${barberId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                loadData();
            } else {
                alert('Error al eliminar barbero');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al eliminar barbero');
        }
    };

    // Funciones para gestión de módulos
    const handleOpenModuleModal = async (barber) => {
        setSelectedBarberForModules(barber);
        setShowModuleModal(true);
        await loadModulesForBarber(barber.id);
        await loadAllModules();
    };

    const loadAllModules = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/barber/service-modules`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const modules = await response.json();
                setAllModules(modules);
            }
        } catch (error) {
            console.error('Error loading modules:', error);
        }
    };

    const loadModulesForBarber = async (barberId) => {
        try {
            setLoadingModules(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/admin/barbers/${barberId}/modules`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const modules = await response.json();
                setBarberModules(modules);
            }
        } catch (error) {
            console.error('Error loading barber modules:', error);
        } finally {
            setLoadingModules(false);
        }
    };

    const toggleBarberModule = async (moduleId, isAssigned) => {
        if (!selectedBarberForModules) return;

        try {
            const token = localStorage.getItem('token');
            const url = `${API_BASE_URL}/admin/barbers/${selectedBarberForModules.id}/modules/${moduleId}`;

            const response = await fetch(url, {
                method: isAssigned ? 'DELETE' : 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                await loadModulesForBarber(selectedBarberForModules.id);
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Error al actualizar módulo');
            }
        } catch (error) {
            console.error('Error toggling module:', error);
            alert('Error al actualizar módulo');
        }
    };

    // Funciones para horarios de descanso por barbero
    const DAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    const handleOpenBreakHoursModal = async (barber) => {
        setSelectedBarberForBreaks(barber);
        setShowBreakHoursModal(true);
        await loadBarberBreakHours(barber.id);
    };

    const loadBarberBreakHours = async (barberId) => {
        try {
            setLoadingBreakHours(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/admin/barbers/${barberId}/break-hours`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setBarberBreakHours((Array.isArray(data) ? data : []).map((b) => ({
                    day_of_week: b.day_of_week ?? 0,
                    start_time: (b.start_time || '12:00').substring(0, 5),
                    end_time: (b.end_time || '14:00').substring(0, 5),
                })));
            } else {
                setBarberBreakHours([]);
            }
        } catch (err) {
            console.error('Error cargando horarios de descanso:', err);
            setBarberBreakHours([]);
        } finally {
            setLoadingBreakHours(false);
        }
    };

    const addBarberBreak = () => {
        setBarberBreakHours((prev) => [...prev, { day_of_week: 0, start_time: '12:00', end_time: '14:00' }]);
    };

    const removeBarberBreak = (index) => {
        setBarberBreakHours((prev) => prev.filter((_, i) => i !== index));
    };

    const updateBarberBreak = (index, field, value) => {
        setBarberBreakHours((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const handleSaveBarberBreaks = async () => {
        if (!selectedBarberForBreaks) return;
        const token = localStorage.getItem('token');
        if (!token) {
            alert('Debes iniciar sesión para guardar');
            return;
        }
        setLoadingBreakHours(true);
        try {
            const payload = barberBreakHours.map((b) => ({
                day_of_week: typeof b.day_of_week === 'number' ? b.day_of_week : parseInt(b.day_of_week, 10),
                start_time: (b.start_time || '12:00').substring(0, 5),
                end_time: (b.end_time || '14:00').substring(0, 5),
            }));
            const res = await fetch(`${API_BASE_URL}/admin/barbers/${selectedBarberForBreaks.id}/break-hours`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ breaks: payload }),
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.detail || 'Error al guardar');
            }
            alert('Horarios de descanso guardados correctamente');
            await loadBarberBreakHours(selectedBarberForBreaks.id);
        } catch (err) {
            alert(err.message || 'Error al guardar horarios de descanso');
        } finally {
            setLoadingBreakHours(false);
        }
    };

    const DAY_NAMES_EXT = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    const handleOpenExtendedHoursModal = async (barber) => {
        setSelectedBarberForExtendedHours(barber);
        setShowExtendedHoursModal(true);
        await loadBarberExtendedHours(barber.id);
    };

    const loadBarberExtendedHours = async (barberId) => {
        try {
            setLoadingExtendedHours(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/admin/barbers/${barberId}/extended-hours`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const byDay = (Array.isArray(data) ? data : []).reduce((acc, h) => { acc[h.day_of_week] = (h.end_time || '').substring(0, 5); return acc; }, {});
                setBarberExtendedHours(DAY_NAMES_EXT.map((_, i) => ({ day_of_week: i, end_time: byDay[i] || '' })));
            } else {
                setBarberExtendedHours(DAY_NAMES_EXT.map((_, i) => ({ day_of_week: i, end_time: '' })));
            }
        } catch (err) {
            console.error('Error cargando horario extendido:', err);
            setBarberExtendedHours(DAY_NAMES_EXT.map((_, i) => ({ day_of_week: i, end_time: '' })));
        } finally {
            setLoadingExtendedHours(false);
        }
    };

    const updateBarberExtendedHour = (index, value) => {
        setBarberExtendedHours((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], end_time: value };
            return next;
        });
    };

    const handleSaveBarberExtendedHours = async () => {
        if (!selectedBarberForExtendedHours) return;
        const token = localStorage.getItem('token');
        if (!token) {
            alert('Debes iniciar sesión para guardar');
            return;
        }
        setLoadingExtendedHours(true);
        try {
            const payload = barberExtendedHours
                .filter((h) => (h.end_time || '').trim() !== '')
                .map((h) => ({
                    day_of_week: h.day_of_week,
                    end_time: (h.end_time || '20:00').substring(0, 5),
                }));
            const res = await fetch(`${API_BASE_URL}/admin/barbers/${selectedBarberForExtendedHours.id}/extended-hours`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ hours: payload }),
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.detail || 'Error al guardar');
            }
            alert('Horario extendido guardado correctamente');
            await loadBarberExtendedHours(selectedBarberForExtendedHours.id);
        } catch (err) {
            alert(err.message || 'Error al guardar horario extendido');
        } finally {
            setLoadingExtendedHours(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(amount);
    };

    // Funciones para agendar citas desde admin
    const handleOpenBookAppointment = () => {
        setEditingAppointmentId(null);
        setShowBookAppointmentModal(true);
        setAppointmentStep('barber');
        setSelectedBarberForAppointment(null);
        setBarberServices([]);
        setSelectedService(null);
        setAvailableTimeSlots([]); // Evitar mostrar slots de otro barbero
        setAppointmentForm({
            client_name: '',
            client_phone: '',
            client_email: '',
            appointment_date: '',
            appointment_time: '',
            duration_minutes: 30,
            price: '',
            notes: ''
        });
        setAppointmentInventoryLines([]);
        setInventoryCategoryFilter('');
    };

    const addProductToAppointmentLine = (product) => {
        if (!product?.id) return;
        const id = product.id;
        const unit = parseFloat(product.price);
        const unitPrice = Number.isFinite(unit) ? unit : 0;
        setAppointmentInventoryLines(prev => {
            const idx = prev.findIndex(l => l.inventory_id === id);
            if (idx >= 0) {
                const next = [...prev];
                const cur = typeof next[idx].quantity === 'number'
                    ? next[idx].quantity
                    : parseInt(String(next[idx].quantity), 10);
                const base = Number.isFinite(cur) && cur >= 1 ? cur : 1;
                next[idx] = { ...next[idx], quantity: base + 1 };
                return next;
            }
            return [...prev, {
                inventory_id: id,
                quantity: 1,
                product_name: product.name || '',
                unit_price: unitPrice
            }];
        });
    };

    const updateAppointmentLineQuantity = (inventoryId, qty) => {
        const raw = String(qty);
        if (raw === '') {
            setAppointmentInventoryLines(prev => prev.map(l =>
                l.inventory_id === inventoryId ? { ...l, quantity: '' } : l
            ));
            return;
        }
        const q = parseInt(raw, 10);
        if (!Number.isFinite(q)) return;
        if (q < 1) return;
        setAppointmentInventoryLines(prev => prev.map(l =>
            l.inventory_id === inventoryId ? { ...l, quantity: q } : l
        ));
    };

    const normalizeAppointmentLineQuantityOnBlur = (inventoryId, rawValue) => {
        const q = parseInt(String(rawValue), 10);
        if (!Number.isFinite(q) || q < 1) {
            setAppointmentInventoryLines(prev => prev.map(l =>
                l.inventory_id === inventoryId ? { ...l, quantity: 1 } : l
            ));
        }
    };

    const removeAppointmentLine = (inventoryId) => {
        setAppointmentInventoryLines(prev => prev.filter(l => l.inventory_id !== inventoryId));
    };

    const getAppointmentDisplayTotal = (apt) => {
        if (!apt) return 0;
        if (apt.appointment_total != null && apt.appointment_total !== '') return Number(apt.appointment_total);
        return Number(apt.service_price || 0) + Number(apt.inventory_subtotal || 0);
    };

    const handleEditAppointment = async (appointment) => {
        setEditingAppointmentId(appointment.id);
        setShowBookAppointmentModal(true);
        const barber = barbers.find(b => b.id === appointment.barber_id) || appointment.users;
        if (!barber) {
            alert('No se encontró el barbero de esta cita.');
            return;
        }
        setSelectedBarberForAppointment(barber);
        setLoadingServices(true);
        let loadedServices = [];
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/admin/barbers/${barber.id}/services`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                loadedServices = await response.json();
                loadedServices = loadedServices || [];
                setBarberServices(loadedServices);
                const service = loadedServices.find(s => (s.name || '').toLowerCase() === (appointment.service_name || '').toLowerCase())
                    || loadedServices[0] || null;
                setSelectedService(service);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingServices(false);
        }
        const dateStr = (appointment.appointment_date || '').toString().split('T')[0];
        const timeStr = (appointment.appointment_time || '').toString().substring(0, 5);
        const serviceMatch = loadedServices.find(s => (s.name || '').toLowerCase() === (appointment.service_name || '').toLowerCase())
            || loadedServices[0];
        const rawSp = appointment.service_price;
        const initialPrice = rawSp != null && rawSp !== ''
            ? String(rawSp)
            : (serviceMatch?.price != null ? String(serviceMatch.price) : '');
        setLoadingAppointmentInventory(true);
        setAppointmentInventoryLines([]);
        setInventoryCategoryFilter('');
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const [itemsRes, invRes, catRes] = await Promise.all([
                fetch(`${API_BASE_URL}/admin/appointments/${appointment.id}/inventory-items`, { headers }),
                fetch(`${API_BASE_URL}/admin/inventory`, { headers }),
                fetch(`${API_BASE_URL}/admin/inventory/categories`, { headers })
            ]);
            if (invRes.ok) {
                const invData = await invRes.json();
                setInventoryCatalog(Array.isArray(invData) ? invData : []);
            }
            if (catRes.ok) {
                const catData = await catRes.json();
                setInventoryCategories(Array.isArray(catData) ? catData : []);
            }
            if (itemsRes.ok) {
                const items = await itemsRes.json();
                const arr = Array.isArray(items) ? items : [];
                setAppointmentInventoryLines(arr.map(it => ({
                    inventory_id: it.inventory_id,
                    quantity: it.quantity || 1,
                    product_name: it.product_name || '',
                    unit_price: parseFloat(it.unit_price) || 0
                })));
            }
        } catch (invErr) {
            console.error(invErr);
        } finally {
            setLoadingAppointmentInventory(false);
        }
        setAppointmentForm({
            client_name: appointment.clients?.name || '',
            client_phone: appointment.clients?.phone || '',
            client_email: appointment.clients?.email || '',
            appointment_date: dateStr,
            appointment_time: timeStr,
            duration_minutes: appointment.duration_minutes || 30,
            price: initialPrice,
            notes: appointment.notes || ''
        });
        setAppointmentStep('datetime');
        if (dateStr && barber.id) {
            const serviceForDuration = loadedServices.find(s => (s.name || '').toLowerCase() === (appointment.service_name || '').toLowerCase())
                || loadedServices[0];
            const duration = appointment.duration_minutes || serviceForDuration?.duration_minutes || 30;
            setLoadingTimeSlots(true);
            try {
                const token = localStorage.getItem('token');
                const excludeParam = appointment.id ? `&exclude_appointment_id=${appointment.id}` : '';
                const url = `${API_BASE_URL}/barber/availability?barber_id=${barber.id}&date_str=${dateStr}&duration=${duration}&use_extended=true${excludeParam}`;
                const res = await fetch(url, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} });
                if (res.ok) {
                    const slots = await res.json();
                    setAvailableTimeSlots(slots);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingTimeSlots(false);
            }
        }
    };

    const handleDeleteAppointment = async (appointmentId) => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar esta cita? Esta acción no se puede deshacer.')) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/admin/appointments/${appointmentId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                setAppointments(prev => prev.filter(apt => apt.id !== appointmentId));
                setAppointmentsPagination(prev => ({ ...prev, total: Math.max(0, (prev.total || 0) - 1) }));
            } else {
                const err = await response.json();
                alert(err.detail || 'Error al eliminar la cita');
            }
        } catch (error) {
            console.error('Error deleting appointment:', error);
            alert('Error al conectar con el servidor');
        }
    };

    const handleSelectBarberForAppointment = async (barber) => {
        setSelectedBarberForAppointment(barber);
        setSelectedService(null);
        setAvailableTimeSlots([]); // Limpiar slots al cambiar barbero
        setAppointmentForm(prev => ({ ...prev, appointment_date: '', appointment_time: '' }));
        setLoadingServices(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/admin/barbers/${barber.id}/services`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const services = await response.json();
                setBarberServices(services || []);
                setAppointmentStep('service');

                // Si no hay servicios, mostrar mensaje informativo
                if (!services || services.length === 0) {
                    alert('Este barbero no tiene módulos asignados o no hay servicios disponibles en sus módulos.');
                }
            } else {
                const errorData = await response.json().catch(() => ({ detail: 'Error desconocido' }));
                console.error('Error al cargar servicios:', errorData);
                alert(`Error al cargar servicios del barbero: ${errorData.detail || 'Error desconocido'}`);
            }
        } catch (error) {
            console.error('Error fetching services:', error);
            alert('Error al conectar con el servidor');
        } finally {
            setLoadingServices(false);
        }
    };

    const handleSelectService = (service) => {
        setSelectedService(service);
        setAppointmentForm(prev => ({ ...prev, duration_minutes: service.duration_minutes || 30 }));
        setAppointmentStep('datetime');
    };

    const reloadAvailableTimeSlots = async (date, durationOverride, { clearTime = false } = {}) => {
        if (clearTime) {
            setAppointmentForm(prev => ({ ...prev, appointment_date: date, appointment_time: '' }));
        } else if (date) {
            setAppointmentForm(prev => ({ ...prev, appointment_date: date }));
        }
        if (!date || !selectedBarberForAppointment || !selectedService) return;
        setLoadingTimeSlots(true);
        try {
            const token = localStorage.getItem('token');
            const duration = durationOverride ?? appointmentForm.duration_minutes ?? selectedService.duration_minutes ?? 30;
            const excludeParam = editingAppointmentId ? `&exclude_appointment_id=${editingAppointmentId}` : '';
            const url = `${API_BASE_URL}/barber/availability?barber_id=${selectedBarberForAppointment.id}&date_str=${date}&duration=${duration}&use_extended=true${excludeParam}`;
            const response = await fetch(url, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} });
            if (response.ok) {
                const slots = await response.json();
                setAvailableTimeSlots(slots);
            }
        } catch (error) {
            console.error('Error fetching time slots:', error);
        } finally {
            setLoadingTimeSlots(false);
        }
    };

    const handleDateChange = async (date) => {
        await reloadAvailableTimeSlots(date, undefined, { clearTime: true });
    };

    const handleSubmitAppointment = async (e) => {
        e.preventDefault();
        if (!selectedBarberForAppointment || !selectedService) return;
        if (!appointmentForm.appointment_time) {
            alert('Selecciona una hora para la cita.');
            return;
        }

        const parsedEditPrice = parseFloat(String(appointmentForm.price ?? '').replace(',', '.'), 10);
        const priceForPayload = editingAppointmentId
            ? (Number.isFinite(parsedEditPrice) ? parsedEditPrice : (selectedService.price ?? null))
            : (selectedService.price ?? null);

        const parsedDuration = parseInt(String(appointmentForm.duration_minutes ?? ''), 10);
        const durationForPayload = Number.isFinite(parsedDuration) && parsedDuration >= 5
            ? parsedDuration
            : (selectedService.duration_minutes || 30);

        const payload = {
            client_name: appointmentForm.client_name,
            client_phone: appointmentForm.client_phone,
            client_email: appointmentForm.client_email || '',
            barber_id: selectedBarberForAppointment.id,
            appointment_date: appointmentForm.appointment_date,
            appointment_time: appointmentForm.appointment_time,
            service_name: selectedService.name,
            service_id: selectedService.id || null,
            price: priceForPayload,
            duration_minutes: durationForPayload,
            notes: appointmentForm.notes
        };
        if (editingAppointmentId) {
            for (const l of appointmentInventoryLines) {
                const q = parseInt(String(l.quantity), 10);
                if (!Number.isFinite(q) || q < 1) {
                    alert('Indica una cantidad válida (mínimo 1) en cada producto de la cita, o quita la línea.');
                    return;
                }
            }
            payload.inventory_lines = appointmentInventoryLines.map(l => ({
                inventory_id: l.inventory_id,
                quantity: parseInt(String(l.quantity), 10)
            }));
        }

        try {
            const token = localStorage.getItem('token');
            if (editingAppointmentId) {
                const response = await fetch(`${API_BASE_URL}/admin/appointments/${editingAppointmentId}`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });
                if (response.ok) {
                    alert('Cita actualizada correctamente');
                    setShowBookAppointmentModal(false);
                    setEditingAppointmentId(null);
                    loadData();
                    setAppointmentStep('barber');
                    setSelectedBarberForAppointment(null);
                    setBarberServices([]);
                    setSelectedService(null);
                    setAppointmentForm({
                        client_name: '',
                        client_phone: '',
                        client_email: '',
                        appointment_date: '',
                        appointment_time: '',
                        duration_minutes: 30,
                        price: '',
                        notes: ''
                    });
                    setAppointmentInventoryLines([]);
                    setInventoryCategoryFilter('');
                } else {
                    const errorData = await response.json();
                    const msg = Array.isArray(errorData.detail)
                        ? errorData.detail.map((d) => d.msg || JSON.stringify(d)).join('\n')
                        : (errorData.detail || 'Error al actualizar la cita');
                    alert(msg);
                }
            } else {
                const response = await fetch(`${API_BASE_URL}/barber/book-appointment`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                if (response.ok) {
                    alert('Cita agendada exitosamente');
                    setShowBookAppointmentModal(false);
                    loadData();
                    setAppointmentStep('barber');
                    setSelectedBarberForAppointment(null);
                    setBarberServices([]);
                    setSelectedService(null);
                    setAppointmentForm({
                        client_name: '',
                        client_phone: '',
                        client_email: '',
                        appointment_date: '',
                        appointment_time: '',
                        duration_minutes: 30,
                        price: '',
                        notes: ''
                    });
                    setAppointmentInventoryLines([]);
                    setInventoryCategoryFilter('');
                } else {
                    const errorData = await response.json();
                    const msg = Array.isArray(errorData.detail)
                        ? errorData.detail.map((d) => d.msg || JSON.stringify(d)).join('\n')
                        : (errorData.detail || 'Error al agendar la cita');
                    alert(msg);
                }
            }
        } catch (error) {
            console.error('Error booking/updating appointment:', error);
            alert('Error al conectar con el servidor');
        }
    };

    const handleSubmitClient = async (e) => {
        e.preventDefault();
        if (!clientForm.name.trim()) {
            alert('El nombre es obligatorio');
            return;
        }
        if (!editingClient) {
            if (!(clientForm.email || '').trim()) {
                alert('El correo es obligatorio para crear un cliente (así podrá iniciar sesión)');
                return;
            }
            if (!(clientForm.password || '').trim()) {
                alert('La contraseña es obligatoria para asignar al cliente');
                return;
            }
        }
        if (editingClient) {
            const pw = (clientForm.password || '').trim();
            if (pw && pw.length < 6) {
                alert('La nueva contraseña debe tener al menos 6 caracteres');
                return;
            }
        }
        const token = localStorage.getItem('token');
        try {
            if (editingClient) {
                const pw = (clientForm.password || '').trim();
                const payload = {
                    name: clientForm.name.trim(),
                    phone: (clientForm.phone || '').trim() || null,
                    email: (clientForm.email || '').trim() || null
                };
                if (pw) payload.password = pw;
                const res = await fetch(`${API_BASE_URL}/admin/clients/${editingClient.id}`, {
                    method: 'PATCH',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                if (!res.ok) {
                    const err = await res.json();
                    throw new Error(err.detail || 'Error al actualizar');
                }
                alert('Cliente actualizado');
            } else {
                const res = await fetch(`${API_BASE_URL}/admin/clients`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: clientForm.name.trim(),
                        phone: (clientForm.phone || '').trim() || '',
                        email: (clientForm.email || '').trim() || '',
                        password: (clientForm.password || '').trim() || undefined
                    })
                });
                if (!res.ok) {
                    const err = await res.json();
                    throw new Error(err.detail || 'Error al crear');
                }
                alert('Cliente creado');
            }
            setShowClientModal(false);
            setEditingClient(null);
            setClientForm({ name: '', phone: '', email: '', password: '' });
            loadClients();
        } catch (err) {
            alert(err.message || 'Error al guardar el cliente');
        }
    };

    const handleDeleteClient = async (client) => {
        if (!window.confirm(`¿Eliminar al cliente "${client.name || 'Sin nombre'}"? Esta acción no se puede deshacer.`)) return;
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/clients/${client.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Error al eliminar');
            }
            alert('Cliente eliminado');
            loadClients();
        } catch (err) {
            alert(err.message || 'Error al eliminar el cliente');
        }
    };

    const handleStatusChange = async (appointmentId, newStatus) => {
        // Si el nuevo estado es "completed", mostrar modal de pago
        if (newStatus === 'completed') {
            // Buscar la cita para obtener el precio
            const appointment = appointments.find(apt => apt.id === appointmentId);
            if (appointment) {
                setAppointmentToComplete(appointmentId);
                setServicePrice(getAppointmentDisplayTotal(appointment));
                setShowPaymentModal(true);
                // Revertir el cambio en el select simplemente no actualizando el estado local
                // React se encargará de volverlo al valor original ya que es un componente controlado
                return;
            }
        }

        // Para otros estados, actualizar directamente
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/admin/appointments/${appointmentId}/status`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (response.ok) {
                // Actualizar el estado localmente para feedback inmediato
                setAppointments(prev => prev.map(apt =>
                    apt.id === appointmentId
                        ? { ...apt, status: newStatus }
                        : apt
                ));
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Error al actualizar el estado');
                // Recargar datos para revertir el cambio visual
                loadData();
            }
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Error al conectar con el servidor');
            // Recargar datos para revertir el cambio visual
            loadData();
        }
    };

    const handleCompleteWithPayment = async () => {
        if (!selectedPaymentMethod) {
            alert('Por favor selecciona un método de pago');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/admin/appointments/${appointmentToComplete}/status`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    status: 'completed',
                    payment_data: {
                        payment_method: selectedPaymentMethod,
                        amount: servicePrice
                    }
                })
            });

            if (response.ok) {
                // Actualizar el estado localmente
                setAppointments(prev => prev.map(apt =>
                    apt.id === appointmentToComplete
                        ? {
                            ...apt,
                            status: 'completed',
                            payment_data: {
                                payment_method: selectedPaymentMethod,
                                amount: servicePrice
                            }
                        }
                        : apt
                ));

                // Buscar info del cliente para el modal de calificación
                const completedAppointment = appointments.find(a => a.id === appointmentToComplete);
                const clientPhone = completedAppointment?.client_phone || '';
                const clientName = completedAppointment?.client_name || completedAppointment?.clients?.name || 'Cliente';

                // Cerrar modal y resetear variables de pago
                setShowPaymentModal(false);
                setAppointmentToComplete(null);
                setSelectedPaymentMethod('');
                setServicePrice(0);

                // Recargar datos para asegurar sincronización (opcional, si causa parpadeo se puede quitar o hacer silencioso)
                // loadData(); // Comentado para evitar recarga de pagina
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Error al completar el servicio');
                loadData();
            }
        } catch (error) {
            console.error('Error completing service:', error);
            alert('Error al conectar con el servidor');
            loadData();
        }
    };

    const getFilteredAnalyticsAppointments = () => {
        if (analyticsBarberFilter === 'ALL') return analyticsAppointments;
        const selected = barbers.find((b) => String(b.id) === String(analyticsBarberFilter));
        return analyticsAppointments.filter((apt) => {
            if (apt.barber_id != null && apt.barber_id !== '') {
                return String(apt.barber_id) === String(analyticsBarberFilter);
            }
            return selected && apt.barber_name === selected.name;
        });
    };

    const buildPaymentMethodsFromAppointments = (apts) => {
        const totals = {
            efectivo: 0,
            nequi: 0,
            bancolombia: 0,
            daviplata: 0,
            otros: 0,
            sin_metodo: 0
        };
        const mainMethods = ['efectivo', 'nequi', 'bancolombia', 'daviplata'];
        for (const apt of apts) {
            const amount = Number(apt.amount || 0);
            if (amount <= 0) continue;
            if (!apt.has_payment_method) {
                totals.sin_metodo += amount;
                continue;
            }
            const method = String(apt.payment_method || '').trim().toLowerCase();
            if (mainMethods.includes(method)) {
                totals[method] += amount;
            } else {
                totals.otros += amount;
            }
        }
        return [
            { method: 'efectivo', total: totals.efectivo, label: 'Efectivo' },
            { method: 'nequi', total: totals.nequi, label: 'Nequi' },
            { method: 'bancolombia', total: totals.bancolombia, label: 'Bancolombia' },
            { method: 'daviplata', total: totals.daviplata, label: 'Daviplata' },
            { method: 'otros', total: totals.otros, label: 'Otros / pendiente' },
            { method: 'sin_metodo', total: totals.sin_metodo, label: 'Total sin método de pago registrado aún' }
        ];
    };

    const renderKPICards = () => {
        const today = getLocalDateString();
        const isTodayOnly =
            dateFilters.startDate === today && dateFilters.endDate === today;
        const filteredApts = getFilteredAnalyticsAppointments();
        const isBarberFiltered = analyticsBarberFilter !== 'ALL';
        const barberIncome = filteredApts.reduce((sum, apt) => sum + Number(apt.amount || 0), 0);
        const selectedBarber = barbers.find((b) => String(b.id) === String(analyticsBarberFilter));
        const incomeLabel = isBarberFiltered
            ? `Ingresos de ${selectedBarber?.name || 'barbero'}`
            : (isTodayOnly ? 'Ingresos del día' : 'Ingresos del mes');
        const incomeValue = isBarberFiltered ? barberIncome : stats.total_revenue;

        return (
        <div className="kpi-grid">
            <div className="kpi-card">
                <div className="kpi-icon revenue">
                    <DollarSign size={22} />
                </div>
                <div className="kpi-content">
                    <p className="kpi-label">{incomeLabel}</p>
                    <h3 className="kpi-value">{formatCurrency(incomeValue)}</h3>
                    {isBarberFiltered ? (
                        <span className="kpi-detail">{filteredApts.length} citas del barbero</span>
                    ) : isTodayOnly ? (
                        <span className="kpi-detail">Todas las citas excepto canceladas</span>
                    ) : (
                        <span className="kpi-trend positive">
                            <TrendingUp size={13} />
                            +15% vs mes anterior
                        </span>
                    )}
                </div>
            </div>

            <div className="kpi-card">
                <div className="kpi-icon average">
                    <Receipt size={22} />
                </div>
                <div className="kpi-content">
                    <p className="kpi-label">Deuda del día</p>
                    <h3 className="kpi-value">
                        {formatCurrency(dailyDebt.total_debt || 0)}
                    </h3>
                    <span className="kpi-detail">
                        {dailyDebt.purchases_count || 0} {dailyDebt.purchases_count === 1 ? 'compra' : 'compras'} a crédito
                    </span>
                </div>
            </div>
        </div>
        );
    };

    const renderBarbersTab = () => (
        <div className="tab-content">
            <div className="tab-header">
                <div>
                    <h2>Equipo de barberos</h2>
                    <p className="tab-subtitle">
                        {barbersSubTab === 'settings'
                            ? 'Ajustes y mensajes del sitio web'
                            : 'Rendimiento y estadísticas financieras'}
                    </p>
                </div>
                {barbersSubTab === 'barbers' && (
                <button className="add-barber-btn" onClick={() => {
                    setEditingBarber(null);
                    setFormData({
                        name: '', email: '', password: '', phone: '', specialty: '',
                        bio: '', image_url: '', experience_years: 0, available: true
                    });
                    setShowAddModal(true);
                }}>
                    <UserPlus size={20} />
                    Agregar barbero
                </button>
                )}
            </div>

            {/* Sub-tabs: Barberos | Clientes */}
            <div className="barbers-subtabs">
                <button
                    type="button"
                    className={`barbers-subtab-btn ${barbersSubTab === 'barbers' ? 'active' : ''}`}
                    onClick={() => setBarbersSubTab('barbers')}
                >
                    <Users size={18} />
                    Barberos
                </button>
                <button
                    type="button"
                    className={`barbers-subtab-btn ${barbersSubTab === 'clients' ? 'active' : ''}`}
                    onClick={() => setBarbersSubTab('clients')}
                >
                    <UserPlus size={18} />
                    Clientes
                </button>
                <button
                    type="button"
                    className={`barbers-subtab-btn ${barbersSubTab === 'settings' ? 'active' : ''}`}
                    onClick={() => setBarbersSubTab('settings')}
                >
                    <Settings size={18} />
                    Configuración
                </button>
            </div>

            {barbersSubTab === 'settings' ? (
                <div className="site-settings-section">
                    <div className="site-settings-card">
                        <div className="site-settings-card-header">
                            <div className="site-settings-icon-badge site-settings-icon-badge-whatsapp">
                                <MessageCircle size={22} />
                            </div>
                            <div>
                                <h3 className="site-settings-title">Mensaje de WhatsApp</h3>
                                <p className="site-settings-subtitle">
                                    Texto que se envía al cliente desde Citas
                                </p>
                            </div>
                        </div>

                        {loadingSettings ? (
                            <div className="site-settings-loading">
                                <div className="site-settings-spinner" />
                                <span>Cargando mensaje...</span>
                            </div>
                        ) : (
                            <>
                                <p className="site-settings-help">
                                    Edita el mensaje y pulsa <strong>Guardar</strong>. El cliente lo recibirá tal cual
                                    al hacer clic en su teléfono en la sección de Citas.
                                </p>

                                <label className="site-settings-label" htmlFor="whatsapp-review-message">
                                    Mensaje guardado
                                </label>
                                <textarea
                                    id="whatsapp-review-message"
                                    className="site-settings-textarea"
                                    rows={8}
                                    value={settingsDraft}
                                    onChange={(e) => setSettingsDraft(e.target.value)}
                                    placeholder="Escribe el mensaje de WhatsApp..."
                                />

                                {settingsNotice && (
                                    <div className={`site-settings-notice site-settings-notice-${settingsNotice.type}`}>
                                        {settingsNotice.type === 'success' ? (
                                            <CheckCircle2 size={18} />
                                        ) : (
                                            <AlertCircle size={18} />
                                        )}
                                        <span>{settingsNotice.message}</span>
                                    </div>
                                )}

                                <div className="site-settings-actions">
                                    <button
                                        type="button"
                                        className="site-settings-save-btn"
                                        onClick={saveWhatsappReviewMessage}
                                        disabled={savingSettings}
                                    >
                                        <Save size={18} />
                                        {savingSettings ? 'Guardando...' : 'Guardar mensaje'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            ) : barbersSubTab === 'clients' ? (
                /* Vista Clientes: tabla + buscador + acciones */
                <div className="clients-section">
                    <div className="clients-toolbar">
                        <div className="clients-search-wrap">
                            <Search size={20} />
                            <input
                                type="text"
                                className="clients-search-input"
                                placeholder="Buscar por nombre, teléfono o correo..."
                                value={clientsSearch}
                                onChange={(e) => setClientsSearch(e.target.value)}
                            />
                        </div>
                        <button
                            type="button"
                            className="add-barber-btn"
                            onClick={() => {
                                setEditingClient(null);
                                setClientForm({ name: '', phone: '', email: '', password: '' });
                                setShowClientModal(true);
                            }}
                        >
                            <UserPlus size={20} />
                            Crear cliente
                        </button>
                    </div>
                    <p className="clients-count">
                        {clientsTotal > 0
                            ? `Mostrando ${(clientsPage - 1) * CLIENTS_PAGE_SIZE + 1}-${Math.min(clientsPage * CLIENTS_PAGE_SIZE, clientsTotal)} de ${clientsTotal} clientes`
                            : 'No hay clientes'}
                    </p>
                    {loadingClients ? (
                        <div className="clients-loading"><p>Cargando clientes...</p></div>
                    ) : (
                        <div className="clients-table-wrap">
                            <table className="clients-table clients-directory-table">
                                <thead>
                                    <tr>
                                        <th className="clients-col-name">Nombre</th>
                                        <th className="clients-col-phone">Teléfono</th>
                                        <th className="clients-col-email">Correo</th>
                                        <th className="clients-col-actions">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {clientsList.length === 0 ? (
                                        <tr><td colSpan={4} className="clients-empty">No hay clientes</td></tr>
                                    ) : (
                                        clientsList.map((c) => (
                                            <tr key={c.id}>
                                                <td className="clients-col-name" data-label="Nombre">{c.name || '—'}</td>
                                                <td className="clients-col-phone" data-label="Teléfono">{c.phone || '—'}</td>
                                                <td className="clients-col-email" data-label="Correo">{c.email || '—'}</td>
                                                <td className="clients-actions-cell" data-label="Acciones">
                                                    <div className="clients-actions-row" role="group" aria-label="Acciones del cliente">
                                                        <button
                                                            type="button"
                                                            className="clients-icon-btn clients-icon-btn--edit"
                                                            title="Editar cliente"
                                                            aria-label="Editar cliente"
                                                            onClick={() => {
                                                                setEditingClient(c);
                                                                setClientForm({ name: c.name || '', phone: c.phone || '', email: c.email || '', password: '' });
                                                                setShowClientModal(true);
                                                            }}
                                                        >
                                                            <Edit size={18} strokeWidth={2} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="clients-icon-btn clients-icon-btn--citas"
                                                            title="Ver citas"
                                                            aria-label="Ver citas del cliente"
                                                            onClick={() => openClientAppointmentsModal(c)}
                                                        >
                                                            <CalendarDays size={18} strokeWidth={2} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="clients-icon-btn clients-icon-btn--assign"
                                                            title="Asignar cita"
                                                            aria-label="Asignar nueva cita"
                                                            onClick={() => {
                                                                setSelectedClientForBooking(c);
                                                                setShowMultiAppointmentModal(true);
                                                            }}
                                                        >
                                                            <CalendarPlus size={18} strokeWidth={2} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="clients-icon-btn clients-icon-btn--delete"
                                                            title="Eliminar cliente"
                                                            aria-label="Eliminar cliente"
                                                            onClick={() => handleDeleteClient(c)}
                                                        >
                                                            <Trash2 size={18} strokeWidth={2} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {barbersSubTab === 'clients' && clientsTotal > 0 && (() => {
                        const clientsTotalPages = Math.max(1, Math.ceil(clientsTotal / CLIENTS_PAGE_SIZE));
                        const clientsPageNums = getVisiblePageNumbers(clientsPage, clientsTotalPages);
                        return (
                        <div className="clients-pagination">
                            <span className="clients-pagination-info">
                                Página {clientsPage} de {clientsTotalPages} ({clientsTotal} clientes)
                            </span>
                            <div className="clients-pagination-controls">
                                <button
                                    type="button"
                                    className="clients-pagination-btn"
                                    disabled={clientsPage <= 1 || loadingClients}
                                    onClick={() => setClientsPage(p => Math.max(1, p - 1))}
                                >
                                    Anterior
                                </button>
                                <div className="clients-page-nums" role="navigation" aria-label="Páginas de clientes">
                                    {clientsPageNums.map((item, idx) =>
                                        item === 'ellipsis' ? (
                                            <span key={`cl-ellipsis-${idx}`} className="clients-page-ellipsis" aria-hidden>…</span>
                                        ) : (
                                            <button
                                                key={item}
                                                type="button"
                                                className={`clients-page-num${item === clientsPage ? ' is-active' : ''}`}
                                                disabled={loadingClients}
                                                onClick={() => setClientsPage(item)}
                                                aria-current={item === clientsPage ? 'page' : undefined}
                                            >
                                                {item}
                                            </button>
                                        )
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="clients-pagination-btn clients-pagination-btn-primary"
                                    disabled={clientsPage >= clientsTotalPages || loadingClients}
                                    onClick={() => setClientsPage(p => Math.min(clientsTotalPages, p + 1))}
                                >
                                    Siguiente
                                </button>
                            </div>
                        </div>
                        );
                    })()}
                </div>
            ) : (
                <>
                <div className="barbers-grid">
                {barbers.map(barber => {
                    const barberRevenue = revenueData.find(r => r.barber_id === barber.id) || {};
                    return (
                        <div key={barber.id} className="barber-card-modern">
                            <div className="barber-card-header">
                                <div className="barber-avatar">
                                    {barber.image_url ? (
                                        <img src={barber.image_url.startsWith('http') ? barber.image_url : `${API_BASE_URL}${barber.image_url}`} alt={barber.name} />
                                    ) : (
                                        <div className="avatar-placeholder">
                                            {barber.name.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                </div>
                                <div className="barber-status">
                                    <span className={`status-badge ${barber.available ? 'active' : 'inactive'}`}>
                                        {barber.available ? 'Activo' : 'Inactivo'}
                                    </span>
                                </div>
                            </div>

                            <div className="barber-card-body">
                                <h3 className="barber-name">{barber.name}</h3>
                                <p className="barber-specialty">
                                    <Briefcase size={14} />
                                    {barber.specialty || 'Sin especialidad'}
                                </p>

                                <div className="barber-metrics">
                                    <div className="metric-item">
                                        <span className="metric-label">Cortes totales</span>
                                        <span className="metric-value">{barberRevenue.total_cuts || 0}</span>
                                    </div>
                                    <div className="metric-item">
                                        <span className="metric-label">Ingresos mes</span>
                                        <span className="metric-value revenue">
                                            {formatCurrency(barberRevenue.total_revenue || 0)}
                                        </span>
                                    </div>
                                    <div className="metric-item">
                                        <span className="metric-label">Citas hoy</span>
                                        <span className="metric-value">
                                            <Calendar size={14} />
                                            {barberRevenue.appointments_today || 0}
                                        </span>
                                    </div>
                                    <div className="metric-item">
                                        <span className="metric-label">Rating</span>
                                        <span className="metric-value rating">
                                            <Star size={14} fill="#ef4444" />
                                            {barber.rating || '0.0'}
                                        </span>
                                    </div>
                                </div>

                                <div className="barber-info">
                                    {barber.phone && (
                                        <div className="info-item">
                                            <Phone size={14} />
                                            <span>{barber.phone}</span>
                                        </div>
                                    )}
                                    {barber.email && (
                                        <div className="info-item">
                                            <Mail size={14} />
                                            <span>{barber.email}</span>
                                        </div>
                                    )}
                                    {barber.experience_years > 0 && (
                                        <div className="info-item">
                                            <Activity size={14} />
                                            <span>{barber.experience_years} años de experiencia</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="barber-card-actions">
                                <button className="action-btn" onClick={() => {
                                    setSelectedBarberForCut(barber);
                                    setManualCutForm({ service_name: '', client_name: '', price: '', payment_method: 'efectivo', amount: '', notes: '' });
                                    setShowManualCutModal(true);
                                }} style={{ backgroundColor: '#10b981', color: 'white' }}>
                                    <Scissors size={16} />
                                    <span>Registrar Corte</span>
                                </button>
                                <button className="action-btn" onClick={() => handleOpenModuleModal(barber)} style={{ backgroundColor: '#8b5cf6', color: 'white' }}>
                                    <Settings size={16} />
                                    <span>Módulos</span>
                                </button>
                                <button className="action-btn" onClick={() => handleOpenBreakHoursModal(barber)} style={{ backgroundColor: '#f59e0b', color: 'white' }} title="Horarios de almuerzo/descanso">
                                    <UtensilsCrossed size={16} />
                                    <span>Descanso</span>
                                </button>
                                <button className="action-btn" onClick={() => handleOpenExtendedHoursModal(barber)} style={{ backgroundColor: '#6366f1', color: 'white' }} title="Hasta qué hora puede agendar este barbero cada día (solo en su perfil)">
                                    <Clock size={16} />
                                    <span>Horario tarde</span>
                                </button>
                                <button className="action-btn edit" onClick={() => handleEdit(barber)}>
                                    <Edit size={16} />
                                    <span>Editar</span>
                                </button>
                                <button className="action-btn delete" onClick={() => handleDelete(barber.id)}>
                                    <Trash2 size={16} />
                                    <span>Eliminar</span>
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {barbers.length === 0 && !loading && (
                <div className="empty-state">
                    <Users size={48} />
                    <p>No hay barberos registrados</p>
                    <button className="add-barber-btn" onClick={() => setShowAddModal(true)}>
                        <UserPlus size={20} />
                        Agregar primer barbero
                    </button>
                </div>
            )}
            </>
            )}
        </div>
    );

    const renderAppointmentsTab = () => {
        const DAYS_LABELS = [
            { value: 'all', label: 'Todas' },
            { value: 0, label: 'Dom' },
            { value: 1, label: 'Lun' },
            { value: 2, label: 'Mar' },
            { value: 3, label: 'Mié' },
            { value: 4, label: 'Jue' },
            { value: 5, label: 'Vie' },
            { value: 6, label: 'Sáb' }
        ];

        const getDayOfWeek = (dateStr) => {
            if (!dateStr) return -1;
            const d = new Date(dateStr + 'T12:00:00');
            return d.getDay();
        };

        const searchLower = appointmentSearchQuery.trim().toLowerCase();
        const dayFilter = appointmentDayFilter === 'all' ? null : Number(appointmentDayFilter);

        const filteredAppointments = appointments.filter((apt) => {
            if (dayFilter !== null && getDayOfWeek(apt.appointment_date) !== dayFilter) return false;
            if (!searchLower) return true;
            const name = (apt.clients?.name || '').toLowerCase();
            const email = (apt.clients?.email || '').toLowerCase();
            const phone = (apt.clients?.phone || '').replace(/\s/g, '');
            const queryNorm = searchLower.replace(/\s/g, '');
            return name.includes(searchLower) || email.includes(searchLower) || phone.includes(queryNorm);
        });

        const getStatusBadgeClass = (status) => {
            const statusClasses = {
                'pending': 'status-pending',
                'confirmed': 'status-confirmed',
                'completed': 'status-completed',
                'cancelled': 'status-cancelled'
            };
            return statusClasses[status] || 'status-pending';
        };

        const getStatusText = (status) => {
            const statusTexts = {
                'pending': 'Pendiente',
                'confirmed': 'Confirmada',
                'completed': 'Completada',
                'cancelled': 'Cancelada'
            };
            return statusTexts[status] || status;
        };

        const formatTime = (timeString) => {
            if (!timeString) return 'N/A';
            return timeString.substring(0, 5);
        };

        const renderAdminAppointmentsPagination = (placement) => {
            if (
                appointmentDayFilter !== 'all' ||
                appointmentSearchQuery.trim() ||
                appointmentsPagination.total <= APPOINTMENTS_PAGE_SIZE
            ) {
                return null;
            }
            const aptTotalPages = Math.max(1, Math.ceil(appointmentsPagination.total / APPOINTMENTS_PAGE_SIZE));
            const aptPageNumbers = getVisiblePageNumbers(appointmentsPagination.currentPage, aptTotalPages);
            return (
                <div
                    className={`appointments-pagination${placement === 'top' ? ' appointments-pagination-admin-top' : ' appointments-pagination-admin-bottom'}`}
                >
                    <span className="appointments-pagination-info">
                        Página {appointmentsPagination.currentPage} de {aptTotalPages} ({appointmentsPagination.total} citas)
                    </span>
                    <div className="appointments-pagination-btns appointments-pagination-numbered">
                        <button
                            type="button"
                            className="appointments-page-btn"
                            disabled={appointmentsPagination.currentPage <= 1}
                            onClick={() => loadAppointmentsPage(appointmentsPagination.currentPage - 1)}
                        >
                            Anterior
                        </button>
                        <div className="appointments-page-nums" role="navigation" aria-label="Páginas de citas">
                            {aptPageNumbers.map((item, idx) =>
                                item === 'ellipsis' ? (
                                    <span
                                        key={`${placement}-apt-ellipsis-${idx}`}
                                        className="appointments-page-ellipsis"
                                        aria-hidden
                                    >
                                        …
                                    </span>
                                ) : (
                                    <button
                                        key={`${placement}-apt-${item}`}
                                        type="button"
                                        className={`appointments-page-num${item === appointmentsPagination.currentPage ? ' is-active' : ''}`}
                                        onClick={() => loadAppointmentsPage(item)}
                                        aria-current={item === appointmentsPagination.currentPage ? 'page' : undefined}
                                    >
                                        {item}
                                    </button>
                                )
                            )}
                        </div>
                        <button
                            type="button"
                            className="appointments-page-btn appointments-page-btn-primary"
                            disabled={appointmentsPagination.currentPage >= aptTotalPages}
                            onClick={() => loadAppointmentsPage(appointmentsPagination.currentPage + 1)}
                        >
                            Siguiente
                        </button>
                    </div>
                </div>
            );
        };

        return (
            <div className="tab-content">
                <div className="tab-header">
                    <div>
                        <h2>Todas las citas</h2>
                        <p className="tab-subtitle">Gestión completa de citas del sistema</p>
                    </div>
                    <button className="add-barber-btn" onClick={handleOpenBookAppointment}>
                        <Calendar size={20} />
                        Agendar citas
                    </button>
                </div>

                <div className="appointments-filters-bar">
                    <div className="appointments-search-wrap">
                        <Search size={18} className="appointments-search-icon" />
                        <input
                            type="text"
                            className="appointments-search-input"
                            placeholder="Buscar por nombre, correo o teléfono..."
                            value={appointmentSearchQuery}
                            onChange={(e) => setAppointmentSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="appointments-days-tabs">
                        {DAYS_LABELS.map(({ value, label }) => (
                            <button
                                key={value}
                                type="button"
                                className={`appointments-day-tab ${appointmentDayFilter === value ? 'active' : ''}`}
                                onClick={() => setAppointmentDayFilter(value)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {renderAdminAppointmentsPagination('top')}

                {filteredAppointments.length === 0 ? (
                    <div className="empty-state">
                        <Calendar size={48} />
                        <p>
                            {appointments.length === 0
                                ? 'No hay citas registradas'
                                : 'No hay citas que coincidan con el filtro o la búsqueda'}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Vista móvil: cards por cita */}
                        <div className="appointments-mobile-cards">
                            {filteredAppointments.map(appointment => (
                                <div key={appointment.id} className="appointment-mobile-card">
                                    <div className="appointment-mobile-card-header">
                                        <span className="appointment-mobile-client">
                                            {appointment.clients?.name || 'Cliente desconocido'}
                                        </span>
                                        <span className={`appointment-mobile-status status-${appointment.status || 'pending'}`}>
                                            {appointment.status === 'confirmed' ? 'Confirmada' : appointment.status === 'pending' ? 'Pendiente' : appointment.status === 'completed' ? 'Completada' : 'Cancelada'}
                                        </span>
                                    </div>
                                    <div className="appointment-mobile-details">
                                        <div className="appointment-mobile-row">
                                            <span className="label">Barbero</span>
                                            <span>{appointment.users?.name || 'N/A'}</span>
                                        </div>
                                        <div className="appointment-mobile-row">
                                            <span className="label">Servicio</span>
                                            <span>{appointment.service_name || 'N/A'}</span>
                                        </div>
                                        <div className="appointment-mobile-row">
                                            <span className="label">Duración</span>
                                            <span>{appointment.duration_minutes || 30} min</span>
                                        </div>
                                        <div className="appointment-mobile-row">
                                            <span className="label">Fecha</span>
                                            <span>{formatAppointmentDate(appointment.appointment_date)}</span>
                                        </div>
                                        <div className="appointment-mobile-row">
                                            <span className="label">Hora</span>
                                            <span>{formatTime(appointment.appointment_time)}</span>
                                        </div>
                                        <div className="appointment-mobile-row">
                                            <span className="label">Total</span>
                                            <span className="price-value">{formatCurrency(getAppointmentDisplayTotal(appointment))}</span>
                                        </div>
                                        {appointment.clients?.phone && (
                                            <div className="appointment-mobile-row">
                                                <span className="label">Teléfono</span>
                                                <span
                                                    className="link"
                                                    onClick={() => openWhatsAppReview(appointment.clients.phone)}
                                                >
                                                    {appointment.clients.phone}
                                                </span>
                                            </div>
                                        )}
                                        {appointment.clients?.email && (
                                            <div className="appointment-mobile-row">
                                                <span className="label">Correo</span>
                                                <span>{appointment.clients.email}</span>
                                            </div>
                                        )}
                                        <div className="appointment-mobile-row appointment-mobile-row-status full-width">
                                            <span className="label">Estado</span>
                                            <select
                                                className={`status-select ${getStatusBadgeClass(appointment.status || 'pending')}`}
                                                value={appointment.status || 'pending'}
                                                onChange={(e) => handleStatusChange(appointment.id, e.target.value)}
                                            >
                                                <option value="pending">Pendiente</option>
                                                <option value="confirmed">Confirmada</option>
                                                <option value="completed">Completada</option>
                                                <option value="cancelled">Cancelada</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="appointment-mobile-actions">
                                        <button
                                            type="button"
                                            onClick={() => handleEditAppointment(appointment)}
                                            className="action-btn edit-btn"
                                        >
                                            <Edit size={18} />
                                            Editar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteAppointment(appointment.id)}
                                            className="action-btn delete-btn"
                                        >
                                            <Trash2 size={18} />
                                            Eliminar
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Vista escritorio: tabla */}
                        <div className="appointments-table-container">
                        <table className="appointments-table">
                            <thead>
                                <tr>
                                    <th className="col-cliente">Cliente</th>
                                    <th className="col-barbero">Barbero</th>
                                    <th className="col-servicio">Servicio</th>
                                    <th className="col-fecha">Fecha / Hora</th>
                                    <th className="col-total">Total</th>
                                    <th className="col-estado">Estado</th>
                                    <th className="col-contacto">Contacto</th>
                                    <th className="col-acciones">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAppointments.map(appointment => (
                                    <tr key={appointment.id}>
                                        <td data-label="Cliente" className="col-cliente">
                                            <div className="client-info">
                                                <span className="client-name">
                                                    {appointment.clients?.name || 'Cliente desconocido'}
                                                </span>
                                            </div>
                                        </td>
                                        <td data-label="Barbero" className="col-barbero">
                                            <span className="barber-name">
                                                {appointment.users?.name || 'N/A'}
                                            </span>
                                        </td>
                                        <td data-label="Servicio" className="col-servicio">
                                            <div className="service-cell">
                                                <span className="service-name">
                                                    {appointment.service_name || 'N/A'}
                                                </span>
                                                <span className="service-duration">
                                                    {appointment.duration_minutes || 30} min
                                                </span>
                                            </div>
                                        </td>
                                        <td data-label="Fecha / Hora" className="col-fecha">
                                            <div className="datetime-cell">
                                                <span
                                                    className="datetime-date"
                                                    title={formatAppointmentDate(appointment.appointment_date)}
                                                >
                                                    {formatAppointmentDateShort(appointment.appointment_date)}
                                                </span>
                                                <span className="datetime-time">
                                                    {formatTime(appointment.appointment_time)}
                                                </span>
                                            </div>
                                        </td>
                                        <td data-label="Total" className="col-total">
                                            <span className="price-value">
                                                {formatCurrency(getAppointmentDisplayTotal(appointment))}
                                            </span>
                                        </td>
                                        <td data-label="Estado" className="col-estado">
                                            <select
                                                className={`status-select ${getStatusBadgeClass(appointment.status || 'pending')}`}
                                                value={appointment.status || 'pending'}
                                                onChange={(e) => handleStatusChange(appointment.id, e.target.value)}
                                            >
                                                <option value="pending">Pendiente</option>
                                                <option value="confirmed">Confirmada</option>
                                                <option value="completed">Completada</option>
                                                <option value="cancelled">Cancelada</option>
                                            </select>
                                        </td>
                                        <td data-label="Contacto" className="col-contacto">
                                            <div className="contact-info">
                                                {appointment.clients?.phone && (
                                                    <div
                                                        className="contact-item contact-item-phone"
                                                        style={{ cursor: 'pointer' }}
                                                        title="Enviar link de calificación por WhatsApp"
                                                        onClick={() => openWhatsAppReview(appointment.clients.phone)}
                                                    >
                                                        <MessageCircle size={14} color="#10b981" />
                                                        <span className="contact-text contact-phone">{appointment.clients.phone}</span>
                                                    </div>
                                                )}
                                                {appointment.clients?.email && (
                                                    <div className="contact-item contact-item-email" title={appointment.clients.email}>
                                                        <Mail size={12} />
                                                        <span className="contact-text contact-email">{appointment.clients.email}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td data-label="Acciones" className="col-acciones">
                                            <div className="appointment-actions">
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditAppointment(appointment)}
                                                    className="action-btn edit-btn action-btn-icon"
                                                    title="Editar cita"
                                                    aria-label="Editar cita"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteAppointment(appointment.id)}
                                                    className="action-btn delete-btn action-btn-icon"
                                                    title="Eliminar cita"
                                                    aria-label="Eliminar cita"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                        {renderAdminAppointmentsPagination('bottom')}
                    </>
                )}
            </div>
        );
    };

    const getPaymentMethodIcon = (method) => {
        const iconProps = { size: 32, strokeWidth: 1.5 };
        const icons = {
            'efectivo': <Banknote {...iconProps} />,
            'nequi': <Smartphone {...iconProps} />,
            'bancolombia': <Building2 {...iconProps} />,
            'daviplata': <CreditCard {...iconProps} />,
            'otros': <Receipt {...iconProps} />,
            'sin_metodo': <AlertCircle {...iconProps} />,
            'pago pendiente': <Clock {...iconProps} />
        };
        return icons[method] || <DollarSign {...iconProps} />;
    };

    const getPaymentMethodColor = (method) => {
        const colors = {
            'efectivo': '#10b981',
            'nequi': '#3b82f6',
            'bancolombia': '#f59e0b',
            'daviplata': '#8b5cf6',
            'otros': '#64748b',
            'sin_metodo': '#ef4444',
            'pago pendiente': '#ef4444'
        };
        return colors[method] || '#6b7280';
    };

    const handleApplyFilters = async () => {
        setLoadingFilters(true);
        try {
            await loadData();
        } finally {
            setLoadingFilters(false);
        }
    };

    const handleExportToExcel = async () => {
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (dateFilters.startDate) params.append('start_date', dateFilters.startDate);
            if (dateFilters.endDate) params.append('end_date', dateFilters.endDate);
            const queryString = params.toString();
            const urlSuffix = queryString ? `?${queryString}` : '';

            const response = await fetch(`${API_BASE_URL}/admin/analytics/export${urlSuffix}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                alert('Error al exportar datos');
                return;
            }

            const data = await response.json();

            // Crear workbook
            const wb = XLSX.utils.book_new();

            // Hoja 1: Resumen
            const summaryData = [
                ['Métrica', 'Valor'],
                ['Ingresos Totales', formatCurrency(stats.total_revenue)],
                ['Total Citas', stats.total_appointments],
                ['Barberos Activos', `${stats.active_barbers}/${stats.total_barbers}`],
                ['Total Compras', data.summary?.total_purchases || 0],
                ['Total Debes', data.summary?.total_debts || 0],
                ['Monto Total Debes', formatCurrency(data.summary?.total_debt_amount || 0)],
                ['Rango de Fechas', `${dateFilters.startDate || 'Todo'} - ${dateFilters.endDate || 'Todo'}`]
            ];
            const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
            XLSX.utils.book_append_sheet(wb, ws1, 'Resumen');

            // Hoja 2: Citas
            if (data.appointments && data.appointments.length > 0) {
                const appointmentsData = data.appointments.map(apt => ({
                    'Fecha': apt.appointment_date || 'N/A',
                    'Hora': apt.appointment_time || 'N/A',
                    'Cliente': apt.client_name || 'N/A',
                    'Teléfono Cliente': apt.client_phone || 'N/A',
                    'Barbero': apt.barber_name || 'N/A',
                    'Servicio': apt.service_name || 'N/A',
                    'Estado': apt.status || 'N/A',
                    'Método de Pago': apt.payment_method || 'N/A',
                    'Monto': apt.payment_amount || 0,
                    'Notas': apt.notes || ''
                }));
                const ws2 = XLSX.utils.json_to_sheet(appointmentsData);
                XLSX.utils.book_append_sheet(wb, ws2, 'Citas');
            }

            // Hoja 3: Compras
            if (data.purchases && data.purchases.length > 0) {
                const purchasesData = data.purchases.map(p => ({
                    'Fecha': p.created_at || 'N/A',
                    'Total': p.total || 0,
                    'Método de Pago': p.payment_method || 'N/A',
                    'Estado': p.payment_status || 'N/A',
                    'Items': p.items || '',
                    'Notas': p.notes || ''
                }));
                const ws3 = XLSX.utils.json_to_sheet(purchasesData);
                XLSX.utils.book_append_sheet(wb, ws3, 'Compras');
            }

            // Hoja 4: Debes (Gastos a Crédito)
            if (data.debts && data.debts.length > 0) {
                const debtsData = data.debts.map(d => ({
                    'Fecha': d.created_at || 'N/A',
                    'Hora': d.created_time || 'N/A',
                    'Descripción': d.description || d.items || 'N/A',
                    'Items': d.items || '',
                    'Total': d.total || 0,
                    'Notas': d.notes || ''
                }));
                const ws4_debts = XLSX.utils.json_to_sheet(debtsData);
                XLSX.utils.book_append_sheet(wb, ws4_debts, 'Debes');
            }

            // Hoja 5: Métodos de Pago
            if (paymentMethodsData && paymentMethodsData.length > 0) {
                const paymentMethodsSheet = paymentMethodsData.map(pm => ({
                    'Método': pm.label || 'N/A',
                    'Total': pm.total || 0
                }));
                const ws5_payments = XLSX.utils.json_to_sheet(paymentMethodsSheet);
                XLSX.utils.book_append_sheet(wb, ws5_payments, 'Métodos de Pago');
            }

            // Hoja 6: Rendimiento por Barbero
            if (revenueData && revenueData.length > 0) {
                const barbersData = revenueData.map(b => ({
                    'Barbero': b.barber_name || 'N/A',
                    'Total Cortes': b.total_cuts || 0,
                    'Ingresos Totales': b.total_revenue || 0,
                    'Citas Hoy': b.appointments_today || 0,
                    'Rating': b.rating || 0
                }));
                const ws6 = XLSX.utils.json_to_sheet(barbersData);
                XLSX.utils.book_append_sheet(wb, ws6, 'Barberos');
            }

            // Generar nombre de archivo
            const dateRange = dateFilters.startDate && dateFilters.endDate
                ? `${dateFilters.startDate}_${dateFilters.endDate}`
                : format(new Date(), 'yyyy-MM-dd');
            const filename = `reporte_analiticas_${dateRange}.xlsx`;

            // Descargar
            XLSX.writeFile(wb, filename);
            alert('Excel exportado exitosamente');

        } catch (error) {
            console.error('Error exporting to Excel:', error);
            alert('Error al exportar a Excel');
        }
    };

    const renderAnalyticsTab = () => {
        const filteredApts = getFilteredAnalyticsAppointments();
        const displayPaymentMethods =
            analyticsBarberFilter === 'ALL'
                ? paymentMethodsData
                : buildPaymentMethodsFromAppointments(filteredApts);
        const barberIncome = filteredApts.reduce((sum, apt) => sum + Number(apt.amount || 0), 0);

        const statusLabels = { pending: 'Pendiente', confirmed: 'Confirmada', completed: 'Completada', cancelled: 'Cancelada' };

        return (
        <div>
            {/* Header */}
            <div className="analytics-section-header">
                <h2>
                    <BarChart3 size={22} color="#ef4444" />
                    Análisis de <span className="accent">Rendimiento</span>
                </h2>
            </div>

            {/* Barra de Filtros */}
            <div className="analytics-filter-bar">
                <div className="analytics-filter-group">
                    <Calendar size={16} color="#ef4444" />
                    <span className="analytics-filter-label">Desde</span>
                    <input
                        type="date"
                        value={dateFilters.startDate}
                        onChange={(e) => setDateFilters({ ...dateFilters, startDate: e.target.value })}
                        className="analytics-date-input"
                    />
                </div>
                <div className="analytics-filter-group">
                    <span className="analytics-filter-label">Hasta</span>
                    <input
                        type="date"
                        value={dateFilters.endDate}
                        onChange={(e) => setDateFilters({ ...dateFilters, endDate: e.target.value })}
                        className="analytics-date-input"
                    />
                </div>
                <div className="analytics-filter-group">
                    <Users size={16} color="#ef4444" />
                    <span className="analytics-filter-label">Barbero</span>
                    <select
                        value={analyticsBarberFilter}
                        onChange={(e) => setAnalyticsBarberFilter(e.target.value)}
                        className="analytics-select"
                    >
                        <option value="ALL">Todos</option>
                        {barbers.map((barber) => (
                            <option key={barber.id} value={barber.id}>{barber.name}</option>
                        ))}
                    </select>
                </div>
                <div className="analytics-filter-actions">
                    <button
                        onClick={handleApplyFilters}
                        disabled={loadingFilters}
                        className="btn-apply-filter"
                    >
                        {loadingFilters ? (
                            <><div className="loading-spinner"></div> Aplicando...</>
                        ) : (
                            <><BarChart3 size={15} /> Aplicar Filtros</>
                        )}
                    </button>
                    <button
                        onClick={handleExportToExcel}
                        className="btn-export-excel"
                    >
                        <Download size={15} /> Exportar Excel
                    </button>
                </div>
            </div>

            {/* Filtros activos */}
            {(dateFilters.startDate || dateFilters.endDate) && (
                <div className="analytics-active-filters">
                    <Calendar size={15} />
                    <strong>Filtros activos:</strong>
                    {dateFilters.startDate && (
                        <span className="filter-badge">Desde: {dateFilters.startDate}</span>
                    )}
                    {dateFilters.endDate && (
                        <span className="filter-badge">Hasta: {dateFilters.endDate}</span>
                    )}
                    {analyticsBarberFilter !== 'ALL' ? (
                        <span className="filter-badge">Barbero: {barbers.find((b) => String(b.id) === String(analyticsBarberFilter))?.name || 'Seleccionado'}</span>
                    ) : (
                        <span className="filter-badge">Todos los barberos</span>
                    )}
                </div>
            )}

            {/* KPI Cards */}
            {renderKPICards()}

            {/* Métodos de Pago */}
            <div className="analytics-payments-section">
                <h3 className="analytics-section-title">
                    Ingresos por Método de Pago
                    {analyticsBarberFilter !== 'ALL' && (
                        <span className="title-badge">solo del barbero</span>
                    )}
                </h3>
                <div className="kpi-grid">
                    {displayPaymentMethods.map(item => (
                        <div key={item.method} className="payment-method-card">
                            <div
                                className="payment-method-icon"
                                style={{ backgroundColor: `${getPaymentMethodColor(item.method)}18`, color: getPaymentMethodColor(item.method) }}
                            >
                                {getPaymentMethodIcon(item.method)}
                            </div>
                            <div className="payment-method-info">
                                <span className="payment-method-name">{item.label}</span>
                                <span className="payment-method-amount" style={{ color: getPaymentMethodColor(item.method) }}>
                                    {formatCurrency(item.total || 0)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Listado de Citas */}
            <div className="analytics-appointments-section">
                <div className="analytics-appointments-header">
                    <h3 className="analytics-section-title" style={{ margin: 0 }}>
                        Citas del período
                        <span className="title-badge">{filteredApts.length} registros</span>
                    </h3>
                    <div className="analytics-total-badge">
                        <DollarSign size={15} />
                        Total barbero: {formatCurrency(barberIncome)}
                    </div>
                </div>

                {filteredApts.length === 0 ? (
                    <div className="analytics-empty-state">
                        <Calendar size={40} />
                        <p>No hay citas para mostrar con estos filtros</p>
                    </div>
                ) : (
                    <>
                        {/* Tabla Desktop */}
                        <div className="analytics-table-wrapper">
                            <table className="analytics-table">
                                <thead>
                                    <tr>
                                        <th>Hora</th>
                                        <th>Cliente</th>
                                        <th>Barbero</th>
                                        <th>Servicio</th>
                                        <th>Estado</th>
                                        <th>Método de pago</th>
                                        <th>Monto</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredApts.map((apt) => {
                                        const methodLabel = apt.has_payment_method
                                            ? (apt.payment_method || '—')
                                            : 'Sin método registrado';
                                        return (
                                            <tr key={apt.id}>
                                                <td style={{ whiteSpace: 'nowrap', color: '#ef4444', fontWeight: 600 }}>
                                                    {apt.appointment_time || '—'}
                                                </td>
                                                <td style={{ fontWeight: 600 }}>{apt.client_name}</td>
                                                <td>{apt.barber_name}</td>
                                                <td>{apt.service_name || '—'}</td>
                                                <td>
                                                    <span className={`analytics-status-badge ${apt.status || ''}`}>
                                                        {statusLabels[apt.status] || apt.status || '—'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`analytics-payment-tag${apt.has_payment_method ? '' : ' no-method'}`}>
                                                        {methodLabel}
                                                    </span>
                                                </td>
                                                <td>{formatCurrency(apt.amount || 0)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Cards Móvil */}
                        <div className="analytics-mobile-cards">
                            {filteredApts.map((apt) => {
                                const methodLabel = apt.has_payment_method
                                    ? (apt.payment_method || '—')
                                    : 'Sin método registrado';
                                return (
                                    <div key={apt.id} className="analytics-appointment-card">
                                        <div className="apc-header">
                                            <span className="apc-time">{apt.appointment_time || '—'}</span>
                                            <span className="apc-amount">{formatCurrency(apt.amount || 0)}</span>
                                        </div>
                                        <div className="apc-body">
                                            <div className="apc-row">
                                                <span className="apc-key">Cliente</span>
                                                <span className="apc-val">{apt.client_name}</span>
                                            </div>
                                            <div className="apc-row">
                                                <span className="apc-key">Barbero</span>
                                                <span className="apc-val">{apt.barber_name}</span>
                                            </div>
                                            <div className="apc-row">
                                                <span className="apc-key">Servicio</span>
                                                <span className="apc-val">{apt.service_name || '—'}</span>
                                            </div>
                                            <div className="apc-row">
                                                <span className="apc-key">Estado</span>
                                                <span className={`analytics-status-badge ${apt.status || ''}`}>
                                                    {statusLabels[apt.status] || apt.status || '—'}
                                                </span>
                                            </div>
                                            <div className="apc-row">
                                                <span className="apc-key">Pago</span>
                                                <span className={`analytics-payment-tag${apt.has_payment_method ? '' : ' no-method'}`}>
                                                    {methodLabel}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
        );
    };

    // Funciones para gestión de servicios
    const handleOpenServiceModal = (service = null) => {
        if (service) {
            setEditingService(service);
            setServiceFormData({
                name: service.name || '',
                description: service.description || '',
                price: service.price || '',
                duration_minutes: service.duration_minutes || '',
                module_id: service.module_id || '',
                active: service.active !== undefined ? service.active : true
            });
        } else {
            setEditingService(null);
            setServiceFormData({
                name: '',
                description: '',
                price: '',
                duration_minutes: '',
                module_id: '',
                active: true
            });
        }
        setShowServiceModal(true);
    };

    const handleOpenServiceModuleModal = (module = null) => {
        if (module) {
            setEditingModule(module);
            setModuleFormData({
                name: module.name || '',
                display_name: module.display_name || '',
                description: module.description || '',
                image_url: module.image_url || '',
                active: module.active !== undefined ? module.active : true,
                display_order: module.display_order || 0
            });
        } else {
            setEditingModule(null);
            setModuleFormData({
                name: '',
                display_name: '',
                description: '',
                image_url: '',
                active: true,
                display_order: 0
            });
        }
        setShowServiceModuleModal(true);
    };

    const handleSaveService = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            const serviceData = {
                name: serviceFormData.name,
                description: serviceFormData.description || null,
                price: parseFloat(serviceFormData.price),
                duration_minutes: parseInt(serviceFormData.duration_minutes),
                module_id: serviceFormData.module_id || null,
                active: serviceFormData.active
            };

            let response;
            if (editingService) {
                response = await fetch(`${API_BASE_URL}/admin/services/${editingService.id}`, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify(serviceData)
                });
            } else {
                response = await fetch(`${API_BASE_URL}/admin/services`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(serviceData)
                });
            }

            if (response.ok) {
                if (editingService) {
                    setServices(prev => prev.map(s => {
                        if (s.id !== editingService.id) return s;
                        const module = serviceData.module_id
                            ? (serviceModules.find(m => m.id === serviceData.module_id) || s.module)
                            : null;
                        return { ...s, ...serviceData, id: s.id, module };
                    }));
                } else {
                    const servicesRes = await fetch(`${API_BASE_URL}/admin/services`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (servicesRes.ok) {
                        const list = await servicesRes.json();
                        setServices(list);
                    }
                }
                setShowServiceModal(false);
                setEditingService(null);
            } else {
                const errorData = await response.json();
                alert(typeof errorData.detail === 'string' ? errorData.detail : (errorData.detail?.message || 'Error al guardar el servicio'));
            }
        } catch (error) {
            console.error('Error saving service:', error);
            alert('Error al conectar con el servidor');
        }
    };

    const handleSaveModule = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            const moduleData = {
                name: moduleFormData.name,
                display_name: moduleFormData.display_name,
                description: moduleFormData.description || null,
                image_url: moduleFormData.image_url || null,
                active: moduleFormData.active,
                display_order: parseInt(moduleFormData.display_order) || 0
            };

            let response;
            if (editingModule) {
                response = await fetch(`${API_BASE_URL}/admin/service-modules/${editingModule.id}`, {
                    method: 'PUT',
                    headers,
                    body: JSON.stringify(moduleData)
                });
            } else {
                response = await fetch(`${API_BASE_URL}/admin/service-modules`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(moduleData)
                });
            }

            if (response.ok) {
                if (editingModule) {
                    const updated = {
                        ...moduleData,
                        id: editingModule.id,
                    };
                    setServiceModules(prev => prev.map(m => m.id === editingModule.id ? { ...m, ...updated } : m));
                } else {
                    const modRes = await fetch(`${API_BASE_URL}/admin/service-modules`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (modRes.ok) {
                        const list = await modRes.json();
                        setServiceModules(list);
                    }
                }
                setShowServiceModuleModal(false);
                setEditingModule(null);
            } else {
                const errorData = await response.json();
                alert(typeof errorData.detail === 'string' ? errorData.detail : (errorData.detail?.message || 'Error al guardar el módulo'));
            }
        } catch (error) {
            console.error('Error saving module:', error);
            alert('Error al conectar con el servidor');
        }
    };

    const handleDeleteService = async (serviceId) => {
        if (!window.confirm('¿Estás seguro de que deseas desactivar este servicio?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/admin/services/${serviceId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                setServices(prev => prev.filter(s => s.id !== serviceId));
            } else {
                const errorData = await response.json();
                const msg = typeof errorData.detail === 'string' ? errorData.detail : (errorData.detail?.message || 'Error al desactivar el servicio');
                alert(msg);
            }
        } catch (error) {
            console.error('Error deleting service:', error);
            alert('Error al conectar con el servidor');
        }
    };

    const handleDeleteModule = async (moduleId) => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar este módulo? Se eliminarán también todos los servicios que pertenecen a este módulo.')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/admin/service-modules/${moduleId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                setServiceModules(prev => prev.filter(m => m.id !== moduleId));
                setServices(prev => prev.filter(s => s.module_id !== moduleId));
            } else {
                const errorData = await response.json();
                const msg = typeof errorData.detail === 'string' ? errorData.detail : (errorData.detail?.message || 'Error al eliminar el módulo');
                alert(msg);
            }
        } catch (error) {
            console.error('Error deleting module:', error);
            alert('Error al conectar con el servidor');
        }
    };

    const renderPendingPaymentsTab = () => (
        <div style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#374151' }}>Cuentas por Cobrar</h3>
                    <p style={{ color: '#6b7280' }}>Servicios finalizados con pago pendiente</p>
                </div>
                <button
                    onClick={loadPendingPayments}
                    style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}
                >
                    <Activity size={16} />
                    Actualizar
                </button>
            </div>

            {loadingPendingPayments ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <div className="loading-spinner"></div>
                    <p>Cargando pendientes...</p>
                </div>
            ) : pendingPayments.length === 0 ? (
                <div className="empty-state">
                    <Check size={48} color="#10b981" />
                    <p>No hay pagos pendientes</p>
                </div>
            ) : (
                <div className="appointments-table-container">
                    <table className="appointments-table">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Cliente</th>
                                <th>Barbero</th>
                                <th>Servicio</th>
                                <th>Monto</th>
                                <th>Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pendingPayments.map(payment => (
                                <tr key={payment.id}>
                                    <td>
                                        {formatAppointmentDate(payment.appointment_date)}
                                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                            {payment.appointment_time?.substring(0, 5)}
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ fontWeight: '600' }}>{payment.client_name || payment.clients?.name || 'Cliente'}</div>
                                        {payment.client_phone && (
                                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{payment.client_phone}</div>
                                        )}
                                    </td>
                                    <td>{payment.users?.name || 'Barbero'}</td>
                                    <td>{payment.service_name}</td>
                                    <td>
                                        <span style={{ color: '#ef4444', fontWeight: 'bold' }}>
                                            {formatCurrency(getAppointmentDisplayTotal(payment))}
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            onClick={() => {
                                                setAppointmentToComplete(payment.id);
                                                setServicePrice(getAppointmentDisplayTotal(payment));
                                                setShowPaymentModal(true);
                                            }}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                backgroundColor: '#10b981',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '0.875rem',
                                                fontWeight: '600'
                                            }}
                                        >
                                            Registrar Pago
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );

    const renderServicesTab = () => {
        // Agrupar servicios por módulo
        const servicesByModule = {};
        services.forEach(service => {
            const moduleId = service.module_id || 'sin-modulo';
            const moduleName = service.module?.display_name || service.module?.name || 'Sin módulo';
            if (!servicesByModule[moduleId]) {
                servicesByModule[moduleId] = {
                    module: service.module || { id: moduleId, name: 'Sin módulo', display_name: 'Sin módulo' },
                    services: []
                };
            }
            servicesByModule[moduleId].services.push(service);
        });

        return (
            <div className="tab-content">
                <div className="services-header">
                    <h2>Gestión de Servicios</h2>
                    <div className="services-header-buttons">
                        <button
                            className="add-barber-btn"
                            onClick={() => handleOpenServiceModuleModal()}
                            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                            <Plus size={18} />
                            Nuevo Módulo
                        </button>
                        <button
                            className="add-barber-btn"
                            onClick={() => handleOpenServiceModal()}
                            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                            <Plus size={18} />
                            Nuevo Servicio
                        </button>
                    </div>
                </div>

                {/* Lista de módulos y servicios */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    {Object.keys(servicesByModule).map(moduleId => {
                        const moduleData = servicesByModule[moduleId];
                        return (
                            <div key={moduleId} className="analytics-card" style={{ padding: '1.5rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <div>
                                        <h3 style={{ margin: 0, color: '#374151' }}>{moduleData.module.display_name || moduleData.module.name}</h3>
                                        {moduleData.module.description && (
                                            <p style={{ margin: '0.5rem 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
                                                {moduleData.module.description}
                                            </p>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button
                                            onClick={() => handleOpenServiceModuleModal(moduleData.module)}
                                            style={{
                                                padding: '0.5rem',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '6px',
                                                backgroundColor: 'white',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <Edit size={16} />
                                        </button>
                                        {moduleData.module.id !== 'sin-modulo' && (
                                            <button
                                                onClick={() => handleDeleteModule(moduleData.module.id)}
                                                style={{
                                                    padding: '0.5rem',
                                                    border: '1px solid #fee2e2',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#fef2f2',
                                                    cursor: 'pointer',
                                                    color: '#dc2626'
                                                }}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
                                    {moduleData.services.map(service => (
                                        <div
                                            key={service.id}
                                            style={{
                                                padding: '1rem',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '8px',
                                                backgroundColor: service.active ? 'white' : '#f9fafb'
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                                                <div>
                                                    <h4 style={{ margin: 0, color: '#374151' }}>{service.name}</h4>
                                                    {service.description && (
                                                        <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
                                                            {service.description}
                                                        </p>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', gap: '0.25rem' }}>
                                                    <button
                                                        onClick={() => handleOpenServiceModal(service)}
                                                        style={{
                                                            padding: '0.25rem',
                                                            border: 'none',
                                                            backgroundColor: 'transparent',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <Edit size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteService(service.id)}
                                                        style={{
                                                            padding: '0.25rem',
                                                            border: 'none',
                                                            backgroundColor: 'transparent',
                                                            cursor: 'pointer',
                                                            color: '#dc2626'
                                                        }}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                            <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem', fontSize: '0.875rem' }}>
                                                <span style={{ color: '#6b7280' }}>
                                                    <DollarSign size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /> {formatCurrency(service.price || 0)}
                                                </span>
                                                <span style={{ color: '#6b7280' }}>
                                                    <Calendar size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /> {service.duration_minutes || 0} min
                                                </span>
                                                <span style={{ color: service.active ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                                                    {service.active ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="admin-dashboard">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Cargando...</p>
                </div>
            </div>
        );
    }

    // Al final del JSX principal, antes del cierre del modal de AddBarber, agregamos el modal de Manual Cut
    return (
        <div className="admin-dashboard">
            {/* ... header and tabs ... */}
            <header className="dashboard-header">
                <div className="header-left">
                    <button
                        type="button"
                        className="mobile-menu-btn"
                        onClick={() => setMobileMenuOpen(true)}
                        aria-label="Abrir menú"
                    >
                        <Menu size={24} />
                    </button>
                    <div className="logo">
                        <Scissors size={28} />
                        <span>GENUINO'S <span className="logo-admin">Admin</span></span>
                    </div>
                </div>
                <div className="header-right">
                    <button className="admin-badge">
                        <Users size={16} />
                        Administrador
                    </button>
                    <button className="logout-btn" onClick={handleLogout}>
                        <LogOut size={18} />
                        Salir
                    </button>
                </div>
            </header>

            <div className="dashboard-banner">
                <h1>Panel de Administración</h1>
                <p>Control total del negocio: barberos, citas y estadísticas financieras</p>
            </div>

            <div className="dashboard-content">
                {error && (
                    <div className="error-banner" style={{
                        backgroundColor: '#fee2e2',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '20px',
                        color: '#991b1b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <span>{error}</span>
                        <button
                            onClick={() => setError(null)}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#991b1b',
                                cursor: 'pointer',
                                fontSize: '18px',
                                padding: '0 8px'
                            }}
                        >
                            ×
                        </button>
                    </div>
                )}

                {/* Overlay y panel del menú móvil (solo visible en vista celular) */}
                {mobileMenuOpen && (
                    <>
                        <div
                            className="mobile-menu-overlay"
                            onClick={() => setMobileMenuOpen(false)}
                            aria-hidden="true"
                        />
                        <div className="mobile-menu-panel">
                            <div className="mobile-menu-header">
                                <span className="mobile-menu-title">Menú</span>
                                <button
                                    type="button"
                                    className="mobile-menu-close"
                                    onClick={() => setMobileMenuOpen(false)}
                                    aria-label="Cerrar menú"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                            <nav className="mobile-menu-nav">
                                <button
                                    className={`mobile-menu-item ${activeTab === 'barbers' ? 'active' : ''}`}
                                    onClick={() => { goToTab('barbers'); setMobileMenuOpen(false); }}
                                >
                                    <Users size={22} />
                                    Barberos
                                </button>
                                <button
                                    className={`mobile-menu-item ${activeTab === 'appointments' ? 'active' : ''}`}
                                    onClick={() => { goToTab('appointments'); setMobileMenuOpen(false); }}
                                >
                                    <Calendar size={22} />
                                    Citas
                                </button>
                                <button
                                    className={`mobile-menu-item ${activeTab === 'analytics' ? 'active' : ''}`}
                                    onClick={() => { goToTab('analytics', 'analytics'); setMobileMenuOpen(false); }}
                                >
                                    <BarChart3 size={22} />
                                    Datos y Compras
                                </button>
                                <button
                                    className={`mobile-menu-item ${activeTab === 'services' ? 'active' : ''}`}
                                    onClick={() => { goToTab('services'); setMobileMenuOpen(false); }}
                                >
                                    <Package size={22} />
                                    Servicios
                                </button>
                                <button
                                    className={`mobile-menu-item ${activeTab === 'inventory' ? 'active' : ''}`}
                                    onClick={() => { goToTab('inventory'); setMobileMenuOpen(false); }}
                                >
                                    <Warehouse size={22} />
                                    Inventario
                                </button>
                                <button
                                    className={`mobile-menu-item ${activeTab === 'horarios' ? 'active' : ''}`}
                                    onClick={() => { goToTab('horarios'); setMobileMenuOpen(false); }}
                                >
                                    <Clock size={22} />
                                    Horarios
                                </button>
                            </nav>
                        </div>
                    </>
                )}

                {/* Botón Agendar Citas destacado */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    margin: '2rem 0',
                    padding: '0 1rem'
                }}>
                    <button
                        className="add-barber-btn"
                        onClick={handleOpenBookAppointment}
                        style={{
                            fontSize: '1.1rem',
                            padding: '1rem 2rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem'
                        }}
                    >
                        <Calendar size={24} />
                        Agendar Nueva Cita
                    </button>
                </div>

                <nav className="dashboard-tabs">
                    <button
                        className={`tab-btn ${activeTab === 'barbers' ? 'active' : ''}`}
                        onClick={() => goToTab('barbers')}
                    >
                        <Users size={20} />
                        Barberos
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'appointments' ? 'active' : ''}`}
                        onClick={() => goToTab('appointments')}
                    >
                        <Calendar size={20} />
                        Citas
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'analytics' ? 'active' : ''}`}
                        onClick={() => goToTab('analytics', 'analytics')}
                    >
                        <BarChart3 size={20} />
                        Datos y Compras
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'services' ? 'active' : ''}`}
                        onClick={() => goToTab('services')}
                    >
                        <Package size={20} />
                        Servicios
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'inventory' ? 'active' : ''}`}
                        onClick={() => goToTab('inventory')}
                    >
                        <Warehouse size={20} />
                        Inventario
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'horarios' ? 'active' : ''}`}
                        onClick={() => goToTab('horarios')}
                    >
                        <Clock size={20} />
                        Horarios
                    </button>
                </nav>

                <main className="dashboard-main">
                    {activeTab === 'barbers' && renderBarbersTab()}
                    {activeTab === 'appointments' && renderAppointmentsTab()}
                    {activeTab === 'analytics' && (
                        <div className="tab-content">
                            {/* Desplegable solo en vista celular */}
                            <div className="sub-tabs-dropdown-mobile">
                                <select
                                    value={activeSubTab}
                                    onChange={(e) => setActiveSubTab(e.target.value)}
                                    className="sub-tabs-select-mobile"
                                    aria-label="Seleccionar sección de Datos y Compras"
                                >
                                    <option value="analytics">Análisis</option>
                                    <option value="compras">Compras</option>
                                    <option value="debe">Debe</option>
                                    <option value="pending_payments">Por Cobrar</option>
                                </select>
                            </div>
                            {/* Botones de sub-tabs: solo escritorio */}
                            <div className="sub-tabs-container">
                                <button
                                    className={`sub-tab-btn ${activeSubTab === 'analytics' ? 'active' : ''}`}
                                    onClick={() => setActiveSubTab('analytics')}
                                >
                                    <BarChart3 size={18} />
                                    Análisis
                                </button>
                                <button
                                    className={`sub-tab-btn ${activeSubTab === 'compras' ? 'active' : ''}`}
                                    onClick={() => setActiveSubTab('compras')}
                                >
                                    <ShoppingCart size={18} />
                                    Compras
                                </button>
                                <button
                                    className={`sub-tab-btn ${activeSubTab === 'debe' ? 'active' : ''}`}
                                    onClick={() => setActiveSubTab('debe')}
                                >
                                    <Receipt size={18} />
                                    Debe
                                </button>
                                <button
                                    className={`sub-tab-btn ${activeSubTab === 'pending_payments' ? 'active' : ''}`}
                                    onClick={() => setActiveSubTab('pending_payments')}
                                >
                                    <Clock size={18} />
                                    Por Cobrar
                                </button>
                            </div>
                            {activeSubTab === 'analytics' && renderAnalyticsTab()}
                            {activeSubTab === 'compras' && <Compras onPurchaseComplete={loadData} />}
                            {activeSubTab === 'debe' && <Debe onDebtComplete={loadData} />}
                            {activeSubTab === 'pending_payments' && renderPendingPaymentsTab()}
                        </div>
                    )}
                    {activeTab === 'services' && renderServicesTab()}
                    {activeTab === 'inventory' && <Inventory />}
                    {activeTab === 'horarios' && <ScheduleAdmin />}
                </main>
            </div>

            {/* Modal Agregar Barbero */}
            {showAddModal && (
                <div className="modal-overlay" onClick={() => {
                    setShowAddModal(false);
                    setEditingBarber(null);
                }}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{editingBarber ? 'Editar Barbero' : 'Agregar Barbero'}</h2>
                        <form onSubmit={handleSubmit}>
                            {/* ... existing form fields ... */}
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Nombre completo *</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        placeholder="Juan Méndez"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Email *</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                        disabled={!!editingBarber}
                                        placeholder="juan@example.com"
                                    />
                                </div>
                            </div>

                            {!editingBarber && (
                                <div className="form-group">
                                    <label>Contraseña *</label>
                                    <input
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        required
                                        placeholder="Mínimo 6 caracteres"
                                    />
                                </div>
                            )}

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Teléfono</label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        placeholder="+57 300 123 4567"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Especialidad</label>
                                    <input
                                        type="text"
                                        value={formData.specialty}
                                        onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                                        placeholder="Cortes Modernos & Fade"
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Años de experiencia</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={formData.experience_years}
                                        onChange={(e) => setFormData({ ...formData, experience_years: parseInt(e.target.value) || 0 })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Imagen del barbero</label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                                            <input
                                                key={barberImageInputKey}
                                                type="file"
                                                accept="image/jpeg,image/png,image/gif,image/webp"
                                                onChange={(e) => {
                                                    const f = e.target.files?.[0];
                                                    setBarberImageFile(f || null);
                                                    if (!f) return;
                                                    setFormData(prev => ({ ...prev, image_url: '' }));
                                                }}
                                                style={{ maxWidth: '100%' }}
                                            />
                                            {barberImageFile && (
                                                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>{barberImageFile.name}</span>
                                            )}
                                        </div>
                                        {(barberImageFile || formData.image_url) && (
                                            <>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                                                    <div style={{ width: '100px', height: '100px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb', background: '#f9fafb' }}>
                                                        {barberImageFile ? (
                                                            <img src={URL.createObjectURL(barberImageFile)} alt="Vista previa" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                        ) : formData.image_url ? (
                                                            <img src={formData.image_url} alt="Vista previa" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                                                        ) : null}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, image_url: '' }));
                                                            setBarberImageFile(null);
                                                            setBarberImageInputKey(k => k + 1);
                                                        }}
                                                        style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', cursor: 'pointer' }}
                                                    >
                                                        Eliminar imagen
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Biografía</label>
                                <textarea
                                    value={formData.bio}
                                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                                    rows="3"
                                    placeholder="Breve descripción del barbero..."
                                />
                            </div>

                            <div className="form-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.available}
                                        onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                                    />
                                    Barbero disponible
                                </label>
                            </div>

                            <div className="modal-actions">
                                <button type="button" onClick={() => {
                                    setShowAddModal(false);
                                    setEditingBarber(null);
                                    setBarberImageFile(null);
                                    setFormData({
                                        name: '', email: '', password: '', phone: '', specialty: '',
                                        bio: '', image_url: '', experience_years: 0, available: true
                                    });
                                }}>
                                    Cancelar
                                </button>
                                <button type="submit" className="submit-btn">
                                    {editingBarber ? 'Actualizar' : 'Crear Barbero'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Registrar Corte Manual */}
            {showManualCutModal && (
                <div className="modal-overlay" onClick={() => setShowManualCutModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Registrar Corte Realizado</h2>
                        <p className="modal-subtitle">Asignar corte a: <strong>{selectedBarberForCut?.name}</strong></p>

                        <form onSubmit={handleManualCutSubmit}>
                            <div className="form-group">
                                <label>Servicio Realizado *</label>
                                <input
                                    type="text"
                                    placeholder="Escriba el servicio realizado..."
                                    value={manualCutForm.service_name}
                                    onChange={(e) => setManualCutForm({ ...manualCutForm, service_name: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Precio del Corte *</label>
                                <input
                                    type="number"
                                    placeholder="Ingrese el precio"
                                    value={manualCutForm.price}
                                    onChange={(e) => setManualCutForm({ ...manualCutForm, price: e.target.value })}
                                    required
                                    min="0"
                                    step="0.01"
                                />
                            </div>

                            <div className="form-group">
                                <label>Método de Pago *</label>
                                <select
                                    value={manualCutForm.payment_method}
                                    onChange={(e) => setManualCutForm({ ...manualCutForm, payment_method: e.target.value })}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '1rem'
                                    }}
                                >
                                    <option value="efectivo">Efectivo</option>
                                    <option value="nequi">Nequi</option>
                                    <option value="bancolombia">Bancolombia</option>
                                    <option value="daviplata">Daviplata</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Monto que pagó *</label>
                                <input
                                    type="number"
                                    placeholder="Monto recibido (si es distinto al precio)"
                                    value={manualCutForm.amount}
                                    onChange={(e) => setManualCutForm({ ...manualCutForm, amount: e.target.value })}
                                    min="0"
                                    step="0.01"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '1rem'
                                    }}
                                />
                                <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>Dejar vacío para usar el precio del corte</small>
                            </div>

                            <div className="form-group">
                                <label>Nombre del Cliente (Opcional)</label>
                                <input
                                    type="text"
                                    placeholder="Cliente Mostrador / Casual"
                                    value={manualCutForm.client_name}
                                    onChange={(e) => setManualCutForm({ ...manualCutForm, client_name: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label>Notas Adicionales</label>
                                <textarea
                                    rows="2"
                                    value={manualCutForm.notes}
                                    onChange={(e) => setManualCutForm({ ...manualCutForm, notes: e.target.value })}
                                />
                            </div>

                            <div className="modal-actions">
                                <button type="button" onClick={() => setShowManualCutModal(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="submit-btn" style={{ backgroundColor: '#10b981' }}>
                                    Registrar Corte
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Gestionar Módulos */}
            {showModuleModal && selectedBarberForModules && (
                <div className="modal-overlay" onClick={() => {
                    setShowModuleModal(false);
                    setSelectedBarberForModules(null);
                    setBarberModules([]);
                }}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <h2>Gestionar Módulos de Servicio</h2>
                        <p className="modal-subtitle">
                            Asignar módulos a: <strong>{selectedBarberForModules.name}</strong>
                        </p>
                        <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '1.5rem' }}>
                            Selecciona los módulos en los que este barbero puede trabajar. Los usuarios solo verán barberos asignados al módulo que seleccionen.
                        </p>

                        {loadingModules ? (
                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                                <div className="loading-spinner"></div>
                                <p>Cargando módulos...</p>
                            </div>
                        ) : (
                            <div className="modules-assignment-list">
                                {allModules.map(module => {
                                    const isAssigned = barberModules.some(bm => bm.id === module.id);
                                    return (
                                        <div
                                            key={module.id}
                                            className={`module-assignment-item ${isAssigned ? 'assigned' : ''}`}
                                            onClick={() => toggleBarberModule(module.id, isAssigned)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="module-assignment-info">
                                                <h4>{module.display_name}</h4>
                                                {module.description && (
                                                    <p>{module.description}</p>
                                                )}
                                            </div>
                                            <div className="module-assignment-toggle">
                                                {isAssigned ? (
                                                    <div style={{
                                                        backgroundColor: '#10b981',
                                                        color: 'white',
                                                        padding: '0.5rem 1rem',
                                                        borderRadius: '8px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        fontWeight: 600
                                                    }}>
                                                        <Check size={18} />
                                                        Asignado
                                                    </div>
                                                ) : (
                                                    <div style={{
                                                        backgroundColor: '#e5e7eb',
                                                        color: '#6b7280',
                                                        padding: '0.5rem 1rem',
                                                        borderRadius: '8px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        fontWeight: 600
                                                    }}>
                                                        <X size={18} />
                                                        No asignado
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div className="modal-actions" style={{ marginTop: '2rem' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowModuleModal(false);
                                    setSelectedBarberForModules(null);
                                    setBarberModules([]);
                                }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Horarios de Descanso por Barbero */}
            {showBreakHoursModal && selectedBarberForBreaks && (
                <div className="modal-overlay" onClick={() => {
                    setShowBreakHoursModal(false);
                    setSelectedBarberForBreaks(null);
                    setBarberBreakHours([]);
                }}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <h2>Horarios de descanso</h2>
                        <p className="modal-subtitle">
                            Configurar almuerzo/descanso para: <strong>{selectedBarberForBreaks.name}</strong>
                        </p>
                        <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '1.5rem' }}>
                            Las horas configuradas quedarán bloqueadas para citas de este barbero. Cada barbero tiene sus propios horarios.
                        </p>

                        {loadingBreakHours ? (
                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                                <div className="loading-spinner"></div>
                                <p>Cargando...</p>
                            </div>
                        ) : (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <button type="button" className="action-btn" onClick={addBarberBreak} style={{ backgroundColor: '#10b981', color: 'white', padding: '0.5rem 1rem' }}>
                                        <Plus size={16} />
                                        Agregar horario
                                    </button>
                                </div>
                                {barberBreakHours.length === 0 ? (
                                    <div style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: '8px', color: '#6b7280', textAlign: 'center' }}>
                                        No hay horarios de descanso. Agrega al menos uno (ej. Lunes a Sábado 12:00–14:00).
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
                                        {barberBreakHours.map((b, index) => (
                                            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                                                <div style={{ flex: 1, display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                    <div style={{ minWidth: '120px' }}>
                                                        <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Día</label>
                                                        <select
                                                            value={b.day_of_week}
                                                            onChange={(e) => updateBarberBreak(index, 'day_of_week', parseInt(e.target.value, 10))}
                                                            style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                                                        >
                                                            {DAY_NAMES.map((name, i) => (
                                                                <option key={name} value={i}>{name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Inicio</label>
                                                        <input
                                                            type="time"
                                                            value={b.start_time}
                                                            onChange={(e) => updateBarberBreak(index, 'start_time', e.target.value)}
                                                            style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Fin</label>
                                                        <input
                                                            type="time"
                                                            value={b.end_time}
                                                            onChange={(e) => updateBarberBreak(index, 'end_time', e.target.value)}
                                                            style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                                                        />
                                                    </div>
                                                </div>
                                                <button type="button" onClick={() => removeBarberBreak(index)} title="Eliminar" style={{ padding: '0.5rem', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}

                        <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                            <button type="button" onClick={() => { setShowBreakHoursModal(false); setSelectedBarberForBreaks(null); setBarberBreakHours([]); }}>
                                Cerrar
                            </button>
                            <button type="button" onClick={handleSaveBarberBreaks} disabled={loadingBreakHours} style={{ backgroundColor: '#10b981', color: 'white', padding: '0.5rem 1.5rem', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                                {loadingBreakHours ? 'Guardando...' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Horario extendido (agendar hasta tarde por día) */}
            {showExtendedHoursModal && selectedBarberForExtendedHours && (
                <div className="modal-overlay" onClick={() => {
                    setShowExtendedHoursModal(false);
                    setSelectedBarberForExtendedHours(null);
                    setBarberExtendedHours([]);
                }}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
                        <h2>Horario tarde</h2>
                        <p className="modal-subtitle">
                            Hasta qué hora puede agendar citas: <strong>{selectedBarberForExtendedHours.name}</strong>
                        </p>
                        <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '1.5rem' }}>
                            Solo aplica en el perfil del barbero. En la reserva pública se sigue usando el cierre normal del local. Deja vacío = sin extender ese día.
                        </p>

                        {loadingExtendedHours ? (
                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                                <div className="loading-spinner"></div>
                                <p>Cargando...</p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
                                {barberExtendedHours.map((h, index) => (
                                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                                        <span style={{ minWidth: '100px', fontWeight: 500 }}>{DAY_NAMES_EXT[h.day_of_week]}</span>
                                        <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Agendar hasta</label>
                                        <input
                                            type="time"
                                            value={h.end_time}
                                            onChange={(e) => updateBarberExtendedHour(index, e.target.value)}
                                            style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                                        />
                                        {h.end_time && (
                                            <button type="button" onClick={() => updateBarberExtendedHour(index, '')} title="Quitar extensión" style={{ padding: '0.25rem', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem' }}>
                                                Limpiar
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                            <button type="button" onClick={() => { setShowExtendedHoursModal(false); setSelectedBarberForExtendedHours(null); setBarberExtendedHours([]); }}>
                                Cerrar
                            </button>
                            <button type="button" onClick={handleSaveBarberExtendedHours} disabled={loadingExtendedHours} style={{ backgroundColor: '#6366f1', color: 'white', padding: '0.5rem 1.5rem', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                                {loadingExtendedHours ? 'Guardando...' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Agendar Citas */}
            {showBookAppointmentModal && (
                <div className="modal-overlay" onClick={() => {
                    setShowBookAppointmentModal(false);
                    setEditingAppointmentId(null);
                    setAppointmentInventoryLines([]);
                    setInventoryCategoryFilter('');
                }}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <h2>{editingAppointmentId ? 'Editar Cita' : 'Agendar Nueva Cita'}</h2>

                        {/* Paso 1: Seleccionar Barbero */}
                        {appointmentStep === 'barber' && (
                            <div>
                                <p className="modal-subtitle">Selecciona el barbero para la cita</p>
                                <div className="barbers-selection-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
                                    {barbers.filter(b => b.available).map(barber => (
                                        <div
                                            key={barber.id}
                                            className="barber-selection-card"
                                            onClick={() => handleSelectBarberForAppointment(barber)}
                                            style={{
                                                padding: '1rem',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '12px',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s',
                                                textAlign: 'center'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#ef4444'}
                                            onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                                        >
                                            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                                                {barber.name.charAt(0).toUpperCase()}
                                            </div>
                                            <h3 style={{ margin: 0, fontSize: '1rem' }}>{barber.name}</h3>
                                        </div>
                                    ))}
                                </div>
                                {loadingServices && <p style={{ textAlign: 'center', marginTop: '1rem' }}>Cargando servicios...</p>}
                            </div>
                        )}

                        {/* Paso 2: Seleccionar Servicio */}
                        {appointmentStep === 'service' && selectedBarberForAppointment && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setAppointmentStep('barber');
                                            setAvailableTimeSlots([]);
                                            setSelectedService(null);
                                            setBarberServices([]);
                                        }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
                                    >
                                        <ArrowLeft size={20} />
                                    </button>
                                    <p className="modal-subtitle" style={{ margin: 0 }}>
                                        Servicios de: <strong>{selectedBarberForAppointment.name}</strong>
                                    </p>
                                </div>
                                {barberServices.length === 0 ? (
                                    <p style={{ textAlign: 'center', color: '#6b7280' }}>
                                        Este barbero no tiene servicios asignados. Por favor, asigna módulos al barbero primero.
                                    </p>
                                ) : (
                                    <div style={{ marginTop: '1rem' }}>
                                        {(() => {
                                            const servicesByModule = barberServices.reduce((acc, service) => {
                                                const moduleId = service.module_id || 'unassigned';

                                                // Determine module name with fallbacks
                                                let moduleName = 'Otros Servicios';
                                                if (service.module) {
                                                    moduleName = service.module.display_name || service.module.name;
                                                } else if (service.module_name) {
                                                    moduleName = service.module_name;
                                                } else if (serviceModules && serviceModules.length > 0) {
                                                    const foundModule = serviceModules.find(m => m.id === service.module_id);
                                                    if (foundModule) moduleName = foundModule.display_name || foundModule.name;
                                                }

                                                if (!acc[moduleId]) {
                                                    acc[moduleId] = {
                                                        id: moduleId,
                                                        name: moduleName,
                                                        services: []
                                                    };
                                                }
                                                acc[moduleId].services.push(service);
                                                return acc;
                                            }, {});

                                            return Object.values(servicesByModule).map(moduleGroup => (
                                                <div key={moduleGroup.id} style={{ marginBottom: '2rem' }}>
                                                    <h3 style={{
                                                        fontSize: '1.1rem',
                                                        fontWeight: '700',
                                                        color: '#374151',
                                                        marginBottom: '1rem',
                                                        borderBottom: '2px solid #f3f4f6',
                                                        paddingBottom: '0.5rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.75rem'
                                                    }}>
                                                        <span style={{
                                                            width: '6px',
                                                            height: '20px',
                                                            backgroundColor: '#ef4444',
                                                            borderRadius: '4px',
                                                            display: 'inline-block'
                                                        }}></span>
                                                        {moduleGroup.name}
                                                        <span style={{
                                                            fontSize: '0.75rem',
                                                            color: '#6b7280',
                                                            backgroundColor: '#f3f4f6',
                                                            padding: '2px 8px',
                                                            borderRadius: '12px',
                                                            fontWeight: '600',
                                                            marginLeft: 'auto'
                                                        }}>
                                                            {moduleGroup.services.length} servicios
                                                        </span>
                                                    </h3>
                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                                                        {moduleGroup.services.map(service => (
                                                            <div
                                                                key={service.id}
                                                                onClick={() => handleSelectService(service)}
                                                                className="service-card-hover"
                                                                style={{
                                                                    padding: '1rem',
                                                                    border: '1px solid #e5e7eb',
                                                                    borderRadius: '12px',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                    backgroundColor: 'white',
                                                                    position: 'relative',
                                                                    overflow: 'hidden'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.borderColor = '#ef4444';
                                                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                                                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                                                    e.currentTarget.style.transform = 'none';
                                                                    e.currentTarget.style.boxShadow = 'none';
                                                                }}
                                                            >
                                                                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', color: '#1f2937', fontWeight: '600' }}>{service.name}</h4>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                                                                    <span style={{ fontWeight: '700', color: '#ef4444', fontSize: '1.1rem' }}>
                                                                        {formatCurrency(service.price)}
                                                                    </span>
                                                                    <span style={{ fontSize: '0.8rem', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                        <Calendar size={12} />
                                                                        {service.duration_minutes} min
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Paso 3: Fecha, Hora y Datos del Cliente */}
                        {appointmentStep === 'datetime' && selectedBarberForAppointment && selectedService && (
                            <form onSubmit={handleSubmitAppointment}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setAppointmentStep('service');
                                            setSelectedService(null);
                                            setAvailableTimeSlots([]);
                                            setAppointmentForm({ ...appointmentForm, appointment_date: '', appointment_time: '' });
                                        }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
                                    >
                                        <ArrowLeft size={20} />
                                    </button>
                                    <p className="modal-subtitle" style={{ margin: 0 }}>
                                        Servicio: <strong>{selectedService.name}</strong>
                                    </p>
                                </div>

                                <div className="form-group">
                                    <label>Nombre del Cliente *</label>
                                    <input
                                        type="text"
                                        value={appointmentForm.client_name}
                                        onChange={(e) => setAppointmentForm(prev => ({ ...prev, client_name: e.target.value }))}
                                        required
                                        placeholder="Nombre completo"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Teléfono *</label>
                                    <input
                                        type="tel"
                                        value={appointmentForm.client_phone}
                                        onChange={(e) => setAppointmentForm(prev => ({ ...prev, client_phone: e.target.value }))}
                                        required
                                        placeholder="+57 300 123 4567"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Email (Opcional)</label>
                                    <input
                                        type="email"
                                        value={appointmentForm.client_email}
                                        onChange={(e) => setAppointmentForm(prev => ({ ...prev, client_email: e.target.value }))}
                                        placeholder="cliente@email.com"
                                    />
                                </div>

                                {editingAppointmentId && (
                                    <div className="form-group">
                                        <label>Precio (COP) *</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="100"
                                            value={appointmentForm.price}
                                            onChange={(e) => setAppointmentForm(prev => ({ ...prev, price: e.target.value }))}
                                            required
                                            placeholder="Ej. 55000"
                                        />
                                        <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                                            Precio del servicio (corte, etc.). El total con productos aparece abajo.
                                        </small>
                                    </div>
                                )}

                                {editingAppointmentId && (
                                    <div className="form-group" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', marginTop: '0.5rem' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                            <Package size={16} aria-hidden /> Productos del inventario (opcional)
                                        </label>
                                        {loadingAppointmentInventory ? (
                                            <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Cargando productos…</p>
                                        ) : (
                                            <>
                                                <div style={{ marginBottom: '0.75rem' }}>
                                                    <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', display: 'block' }}>Categoría</span>
                                                    <select
                                                        value={inventoryCategoryFilter}
                                                        onChange={(e) => setInventoryCategoryFilter(e.target.value)}
                                                        style={{ width: '100%', marginTop: '0.25rem', padding: '0.5rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                                    >
                                                        <option value="">Todas las categorías</option>
                                                        {inventoryCategories.map((c) => (
                                                            <option key={c} value={c}>{c}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem' }}>
                                                    {inventoryCatalog.filter((p) => p.active !== false && (!inventoryCategoryFilter || p.category === inventoryCategoryFilter)).length === 0 ? (
                                                        <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: 0 }}>No hay productos en esta categoría.</p>
                                                    ) : (
                                                        inventoryCatalog
                                                            .filter((p) => p.active !== false && (!inventoryCategoryFilter || p.category === inventoryCategoryFilter))
                                                            .map((p) => (
                                                                <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', padding: '0.35rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                                                    <div style={{ minWidth: 0 }}>
                                                                        <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{p.name}</div>
                                                                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                                            {formatCurrency(p.price || 0)} · Stock {p.stock ?? 0}
                                                                        </div>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => addProductToAppointmentLine(p)}
                                                                        disabled={(p.stock ?? 0) < 1}
                                                                        style={{
                                                                            flexShrink: 0,
                                                                            padding: '0.35rem 0.65rem',
                                                                            fontSize: '0.75rem',
                                                                            borderRadius: '6px',
                                                                            border: '1px solid #3b82f6',
                                                                            background: 'white',
                                                                            color: '#3b82f6',
                                                                            cursor: (p.stock ?? 0) < 1 ? 'not-allowed' : 'pointer',
                                                                            opacity: (p.stock ?? 0) < 1 ? 0.5 : 1
                                                                        }}
                                                                    >
                                                                        Añadir
                                                                    </button>
                                                                </div>
                                                            ))
                                                    )}
                                                </div>
                                                {appointmentInventoryLines.length > 0 && (
                                                    <div style={{ marginTop: '1rem' }}>
                                                        <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.35rem' }}>En esta cita</div>
                                                        <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                                                            <thead>
                                                                <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                                                                    <th style={{ padding: '0.25rem 0' }}>Producto</th>
                                                                    <th>Cant.</th>
                                                                    <th>Subt.</th>
                                                                    <th />
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {appointmentInventoryLines.map((line) => (
                                                                    <tr key={line.inventory_id}>
                                                                        <td style={{ padding: '0.3rem 0', fontWeight: 600 }}>{line.product_name || '—'}</td>
                                                                        <td>
                                                                            <input
                                                                                type="number"
                                                                                inputMode="numeric"
                                                                                value={line.quantity === '' ? '' : line.quantity}
                                                                                onChange={(e) => updateAppointmentLineQuantity(line.inventory_id, e.target.value)}
                                                                                onBlur={() => normalizeAppointmentLineQuantityOnBlur(line.inventory_id, line.quantity)}
                                                                                style={{ width: '3.5rem', padding: '0.2rem' }}
                                                                            />
                                                                        </td>
                                                                        <td>{formatCurrency((line.quantity || 0) * (line.unit_price || 0))}</td>
                                                                        <td>
                                                                            <button type="button" onClick={() => removeAppointmentLine(line.inventory_id)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem' }}>Quitar</button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                                {(() => {
                                                    const prodSub = appointmentInventoryLines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
                                                    const svc = parseFloat(String(appointmentForm.price ?? '').replace(',', '.')) || 0;
                                                    const grand = svc + prodSub;
                                                    return (
                                                        <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', fontSize: '0.875rem' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Servicio</span><span>{formatCurrency(svc)}</span></div>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Productos</span><span>{formatCurrency(prodSub)}</span></div>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: '0.35rem', paddingTop: '0.35rem', borderTop: '1px solid #e5e7eb' }}>
                                                                <span>Total cita</span><span style={{ color: '#ef4444' }}>{formatCurrency(grand)}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                                <small style={{ color: '#6b7280', fontSize: '0.72rem', marginTop: '0.35rem', display: 'block' }}>
                                                    Al guardar se descuenta el stock y se registra el subtotal de productos en la cita.
                                                </small>
                                            </>
                                        )}
                                    </div>
                                )}

                                <div className="form-group">
                                    <label>Duración del servicio (min)</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <div style={{ position: 'relative', width: '130px' }}>
                                            <input
                                                type="number"
                                                value={appointmentForm.duration_minutes}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    const parsed = parseInt(raw, 10);
                                                    const val = isNaN(parsed) ? '' : parsed;
                                                    setAppointmentForm(prev => ({ ...prev, duration_minutes: val }));
                                                    if (val >= 5 && appointmentForm.appointment_date) {
                                                        reloadAvailableTimeSlots(appointmentForm.appointment_date, val, { clearTime: false });
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    const val = Math.max(5, parseInt(e.target.value, 10) || 5);
                                                    setAppointmentForm(prev => ({ ...prev, duration_minutes: val }));
                                                    if (appointmentForm.appointment_date) {
                                                        reloadAvailableTimeSlots(appointmentForm.appointment_date, val, { clearTime: false });
                                                    }
                                                }}
                                                min="5"
                                                max="240"
                                                step="5"
                                                style={{ paddingLeft: '2.5rem', fontWeight: '600', textAlign: 'center' }}
                                            />
                                            <Clock size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                                        </div>
                                        <small style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                                            Por defecto: {selectedService?.duration_minutes || 30} min. Cambia si esta cita dura diferente.
                                        </small>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Fecha *</label>
                                    <input
                                        type="date"
                                        value={appointmentForm.appointment_date}
                                        onChange={(e) => handleDateChange(e.target.value)}
                                        required
                                        min={getLocalDateString()}
                                    />
                                </div>

                                {appointmentForm.appointment_date && (
                                    <div className="form-group">
                                        <label>Hora *</label>
                                        {loadingTimeSlots ? (
                                            <p>Cargando horarios disponibles...</p>
                                        ) : availableTimeSlots.length > 0 ? (
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))', gap: '0.35rem', maxHeight: '280px', overflowY: 'auto', overflowX: 'hidden', padding: '0.25rem' }}>
                                                {getTimeSlotDisplayItems(availableTimeSlots, 5).map((item, idx) => (
                                                    item.type === 'available' ? (
                                                        <button
                                                            key={idx}
                                                            type="button"
                                                            onClick={() => setAppointmentForm(prev => ({ ...prev, appointment_time: item.slot.time_24 }))}
                                                            style={{
                                                                padding: '0.4rem 0.25rem',
                                                                border: `2px solid ${appointmentForm.appointment_time === item.slot.time_24 ? '#3b82f6' : '#e5e7eb'}`,
                                                                borderRadius: '6px',
                                                                background: appointmentForm.appointment_time === item.slot.time_24 ? '#3b82f6' : 'white',
                                                                color: appointmentForm.appointment_time === item.slot.time_24 ? 'white' : '#1f2937',
                                                                cursor: 'pointer',
                                                                fontSize: '0.75rem'
                                                            }}
                                                        >
                                                            {item.slot.time_display}
                                                        </button>
                                                    ) : (
                                                        <div
                                                            key={idx}
                                                            style={{
                                                                padding: '0.4rem 0.25rem',
                                                                border: '2px solid #f3f4f6',
                                                                borderRadius: '6px',
                                                                background: '#f9fafb',
                                                                color: '#9ca3af',
                                                                fontSize: '0.7rem',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gridColumn: 'span 2'
                                                            }}
                                                        >
                                                            {item.startDisplay} - {item.endDisplay} Ocupado
                                                        </div>
                                                    )
                                                ))}
                                            </div>
                                        ) : (
                                            <p style={{ color: '#ef4444' }}>No hay horarios disponibles para esta fecha</p>
                                        )}
                                    </div>
                                )}

                                <div className="form-group">
                                    <label>Notas (Opcional)</label>
                                    <textarea
                                        rows="3"
                                        value={appointmentForm.notes}
                                        onChange={(e) => setAppointmentForm(prev => ({ ...prev, notes: e.target.value }))}
                                        placeholder="Notas adicionales sobre la cita..."
                                    />
                                </div>

                                <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowBookAppointmentModal(false);
                                            setEditingAppointmentId(null);
                                            setAppointmentInventoryLines([]);
                                            setInventoryCategoryFilter('');
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="submit-btn"
                                        disabled={!appointmentForm.appointment_time}
                                        style={{ backgroundColor: editingAppointmentId ? '#3b82f6' : '#10b981' }}
                                        onMouseDown={(e) => e.preventDefault()}
                                    >
                                        {editingAppointmentId ? 'Guardar cambios' : 'Agendar Cita'}
                                    </button>
                                </div>
                            </form>
                        )}

                        {appointmentStep === 'barber' && (
                            <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowBookAppointmentModal(false);
                                        setEditingAppointmentId(null);
                                        setAppointmentInventoryLines([]);
                                        setInventoryCategoryFilter('');
                                    }}
                                >
                                    Cancelar
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modal Crear/Editar Cliente */}
            {showClientModal && (
                <div className="modal-overlay" onClick={() => { setShowClientModal(false); setEditingClient(null); setClientForm({ name: '', phone: '', email: '', password: '' }); }}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
                        <h2>{editingClient ? 'Editar cliente' : 'Crear cliente'}</h2>
                        <form onSubmit={handleSubmitClient}>
                            <div className="form-group">
                                <label>Nombre *</label>
                                <input
                                    type="text"
                                    value={clientForm.name}
                                    onChange={(e) => setClientForm(prev => ({ ...prev, name: e.target.value }))}
                                    required
                                    placeholder="Nombre completo"
                                />
                            </div>
                            <div className="form-group">
                                <label>Teléfono</label>
                                <input
                                    type="tel"
                                    value={clientForm.phone}
                                    onChange={(e) => setClientForm(prev => ({ ...prev, phone: e.target.value }))}
                                    placeholder="300 123 4567"
                                />
                            </div>
                            <div className="form-group">
                                <label>Correo *</label>
                                <input
                                    type="email"
                                    value={clientForm.email}
                                    onChange={(e) => setClientForm(prev => ({ ...prev, email: e.target.value }))}
                                    placeholder="correo@ejemplo.com"
                                    required={!editingClient}
                                />
                            </div>
                            {!editingClient ? (
                                <div className="form-group">
                                    <label>Contraseña *</label>
                                    <input
                                        type="password"
                                        value={clientForm.password}
                                        onChange={(e) => setClientForm(prev => ({ ...prev, password: e.target.value }))}
                                        placeholder="Asignar contraseña para que pueda iniciar sesión"
                                        required
                                        minLength={6}
                                    />
                                </div>
                            ) : (
                                <div className="form-group">
                                    <label>Nueva contraseña</label>
                                    <input
                                        type="password"
                                        value={clientForm.password}
                                        onChange={(e) => setClientForm(prev => ({ ...prev, password: e.target.value }))}
                                        placeholder="Opcional: solo si quieres cambiarla (mín. 6 caracteres)"
                                        autoComplete="new-password"
                                    />
                                    <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                                        El cliente debe tener cuenta de inicio de sesión con este correo.
                                    </p>
                                </div>
                            )}
                            <div className="modal-actions" style={{ marginTop: '1rem' }}>
                                <button type="button" onClick={() => { setShowClientModal(false); setEditingClient(null); setClientForm({ name: '', phone: '', email: '', password: '' }); }}>Cancelar</button>
                                <button type="submit" className="submit-btn" style={{ backgroundColor: '#10b981' }}>{editingClient ? 'Guardar' : 'Crear'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Citas del cliente */}
            {showClientAppointmentsModal && selectedClientForAppointments && (
                <div className="modal-overlay" onClick={() => { setShowClientAppointmentsModal(false); setSelectedClientForAppointments(null); setClientAppointmentsList([]); }}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <h2>Citas de {selectedClientForAppointments.name || 'Cliente'}</h2>
                        <p style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '0.9rem' }}>
                            {selectedClientForAppointments.email || selectedClientForAppointments.phone || ''}
                        </p>
                        {loadingClientAppointments ? (
                            <div style={{ padding: '2rem', textAlign: 'center' }}>
                                <div className="loading-spinner"></div>
                                <p>Cargando citas...</p>
                            </div>
                        ) : clientAppointmentsList.length === 0 ? (
                            <p style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No tiene citas agendadas.</p>
                        ) : (
                            <div style={{ overflowY: 'auto', flex: 1, marginBottom: '1rem' }}>
                                <table className="clients-table" style={{ width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th>Fecha</th>
                                            <th>Hora</th>
                                            <th>Servicio</th>
                                            <th>Barbero</th>
                                            <th>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {clientAppointmentsList.map((apt) => (
                                            <tr key={apt.id}>
                                                <td>{apt.appointment_date || '—'}</td>
                                                <td>{typeof apt.appointment_time === 'string' ? apt.appointment_time : (apt.appointment_time || '—')}</td>
                                                <td>{apt.service_name || '—'}</td>
                                                <td>{apt.barber_name || '—'}</td>
                                                <td>
                                                    <span className={`status-badge status-${apt.status || 'pending'}`} style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}>
                                                        {apt.status === 'completed' ? 'Completada' : apt.status === 'cancelled' ? 'Cancelada' : apt.status === 'confirmed' ? 'Confirmada' : 'Pendiente'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="modal-actions" style={{ marginTop: 'auto', paddingTop: '1rem' }}>
                            <button type="button" onClick={() => { setShowClientAppointmentsModal(false); setSelectedClientForAppointments(null); setClientAppointmentsList([]); }}>
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Pago */}
            {showPaymentModal && (
                <div className="modal-overlay" onClick={() => {
                    setShowPaymentModal(false);
                    setAppointmentToComplete(null);
                    setSelectedPaymentMethod('');
                    setServicePrice(0);
                }}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div style={{
                            backgroundColor: '#ef4444',
                            color: 'white',
                            padding: '1.5rem',
                            borderRadius: '8px 8px 0 0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Finalizar Servicio</h2>
                            <button
                                onClick={() => {
                                    setShowPaymentModal(false);
                                    setAppointmentToComplete(null);
                                    setSelectedPaymentMethod('');
                                    setServicePrice(0);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ padding: '2rem' }}>
                            <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
                                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Valor total del servicio</p>
                                <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ef4444', margin: 0 }}>
                                    {formatCurrency(servicePrice)}
                                </h2>
                            </div>

                            <div style={{ marginBottom: '2rem' }}>
                                <label style={{ display: 'block', marginBottom: '1rem', fontWeight: '600', color: '#374151' }}>
                                    Método de pago *
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                                    {['efectivo', 'nequi', 'bancolombia', 'daviplata', 'pago pendiente'].map(method => (
                                        <button
                                            key={method}
                                            onClick={() => setSelectedPaymentMethod(method)}
                                            style={{
                                                padding: '1rem',
                                                border: selectedPaymentMethod === method ? '2px solid #ef4444' : '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                backgroundColor: selectedPaymentMethod === method ? '#fef2f2' : 'white',
                                                color: selectedPaymentMethod === method ? '#ef4444' : '#374151',
                                                fontWeight: selectedPaymentMethod === method ? '600' : '400',
                                                cursor: 'pointer',
                                                textTransform: 'capitalize',
                                                transition: 'all 0.2s',
                                                fontSize: '0.875rem'
                                            }}
                                        >
                                            {method}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button
                                    onClick={() => {
                                        setShowPaymentModal(false);
                                        setAppointmentToComplete(null);
                                        setSelectedPaymentMethod('');
                                        setServicePrice(0);
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        backgroundColor: 'white',
                                        color: '#374151',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleCompleteWithPayment}
                                    disabled={!selectedPaymentMethod}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem',
                                        border: 'none',
                                        borderRadius: '6px',
                                        backgroundColor: selectedPaymentMethod ? '#ef4444' : '#d1d5db',
                                        color: 'white',
                                        cursor: selectedPaymentMethod ? 'pointer' : 'not-allowed',
                                        fontWeight: '600'
                                    }}
                                >
                                    Finalizar Servicio
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Servicio */}
            {showServiceModal && (
                <div className="modal-overlay" onClick={() => {
                    setShowServiceModal(false);
                    setEditingService(null);
                }}>
                    <div className="modal service-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="service-modal-header" style={{
                            backgroundColor: '#ef4444',
                            color: 'white',
                            padding: '1.5rem',
                            borderRadius: '8px 8px 0 0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h2 style={{ margin: 0 }}>{editingService ? 'Editar Servicio' : 'Nuevo Servicio'}</h2>
                            <button
                                onClick={() => {
                                    setShowServiceModal(false);
                                    setEditingService(null);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div className="service-modal-content" style={{ padding: '2rem' }}>
                            <div className="service-form-field" style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                    Nombre del Servicio *
                                </label>
                                <input
                                    type="text"
                                    value={serviceFormData.name}
                                    onChange={(e) => setServiceFormData({ ...serviceFormData, name: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '1rem'
                                    }}
                                    placeholder="Ej: Corte Premium"
                                />
                            </div>

                            <div className="service-form-field" style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                    Descripción
                                </label>
                                <textarea
                                    value={serviceFormData.description}
                                    onChange={(e) => setServiceFormData({ ...serviceFormData, description: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '1rem',
                                        minHeight: '80px',
                                        resize: 'vertical'
                                    }}
                                    placeholder="Descripción del servicio"
                                />
                            </div>

                            <div className="service-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                        Precio *
                                    </label>
                                    <input
                                        type="number"
                                        value={serviceFormData.price}
                                        onChange={(e) => setServiceFormData({ ...serviceFormData, price: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '6px',
                                            fontSize: '1rem'
                                        }}
                                        placeholder="0"
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                        Duración (minutos) *
                                    </label>
                                    <input
                                        type="number"
                                        value={serviceFormData.duration_minutes}
                                        onChange={(e) => setServiceFormData({ ...serviceFormData, duration_minutes: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '6px',
                                            fontSize: '1rem'
                                        }}
                                        placeholder="30"
                                        min="1"
                                    />
                                </div>
                            </div>

                            <div className="service-form-field" style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                    Módulo
                                </label>
                                <select
                                    value={serviceFormData.module_id}
                                    onChange={(e) => setServiceFormData({ ...serviceFormData, module_id: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '1rem'
                                    }}
                                >
                                    <option value="">Sin módulo</option>
                                    {serviceModules.filter(m => m.active).map(module => (
                                        <option key={module.id} value={module.id}>
                                            {module.display_name || module.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="service-form-field" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input
                                    type="checkbox"
                                    checked={serviceFormData.active}
                                    onChange={(e) => setServiceFormData({ ...serviceFormData, active: e.target.checked })}
                                    id="service-active"
                                />
                                <label htmlFor="service-active" style={{ fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                                    Servicio activo
                                </label>
                            </div>

                            <div className="service-modal-actions" style={{ display: 'flex', gap: '1rem' }}>
                                <button
                                    onClick={() => {
                                        setShowServiceModal(false);
                                        setEditingService(null);
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        backgroundColor: 'white',
                                        color: '#374151',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleSaveService}
                                    disabled={!serviceFormData.name || !serviceFormData.price || !serviceFormData.duration_minutes}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem',
                                        border: 'none',
                                        borderRadius: '6px',
                                        backgroundColor: (!serviceFormData.name || !serviceFormData.price || !serviceFormData.duration_minutes) ? '#d1d5db' : '#ef4444',
                                        color: 'white',
                                        cursor: (!serviceFormData.name || !serviceFormData.price || !serviceFormData.duration_minutes) ? 'not-allowed' : 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    {editingService ? 'Actualizar' : 'Crear'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Módulo */}
            {showServiceModuleModal && (
                <div className="modal-overlay" onClick={() => {
                    setShowServiceModuleModal(false);
                    setEditingModule(null);
                }}>
                    <div className="modal module-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="module-modal-header" style={{
                            backgroundColor: '#ef4444',
                            color: 'white',
                            padding: '1.5rem',
                            borderRadius: '8px 8px 0 0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h2 style={{ margin: 0 }}>{editingModule ? 'Editar Módulo' : 'Nuevo Módulo'}</h2>
                            <button
                                onClick={() => {
                                    setShowServiceModuleModal(false);
                                    setEditingModule(null);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ padding: '2rem' }}>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                    Nombre del Módulo *
                                </label>
                                <input
                                    type="text"
                                    value={moduleFormData.name}
                                    onChange={(e) => setModuleFormData({ ...moduleFormData, name: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '1rem'
                                    }}
                                    placeholder="Ej: corte-modulo"
                                />
                            </div>

                            <div className="module-form-field" style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                    Nombre para Mostrar *
                                </label>
                                <input
                                    type="text"
                                    value={moduleFormData.display_name}
                                    onChange={(e) => setModuleFormData({ ...moduleFormData, display_name: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '1rem'
                                    }}
                                    placeholder="Ej: Módulo de Cortes"
                                />
                            </div>

                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                    Descripción
                                </label>
                                <textarea
                                    value={moduleFormData.description}
                                    onChange={(e) => setModuleFormData({ ...moduleFormData, description: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '1rem',
                                        minHeight: '80px',
                                        resize: 'vertical'
                                    }}
                                    placeholder="Descripción del módulo"
                                />
                            </div>

                            <div className="module-form-field" style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                    URL de Imagen
                                </label>
                                <input
                                    type="text"
                                    value={moduleFormData.image_url}
                                    onChange={(e) => setModuleFormData({ ...moduleFormData, image_url: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        fontSize: '1rem'
                                    }}
                                    placeholder="https://ejemplo.com/imagen.jpg"
                                />
                            </div>

                            <div className="module-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                                        Orden de Visualización
                                    </label>
                                    <input
                                        type="number"
                                        value={moduleFormData.display_order}
                                        onChange={(e) => setModuleFormData({ ...moduleFormData, display_order: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '6px',
                                            fontSize: '1rem'
                                        }}
                                        placeholder="0"
                                        min="0"
                                    />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.75rem' }}>
                                    <input
                                        type="checkbox"
                                        checked={moduleFormData.active}
                                        onChange={(e) => setModuleFormData({ ...moduleFormData, active: e.target.checked })}
                                        id="module-active"
                                    />
                                    <label htmlFor="module-active" style={{ fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                                        Módulo activo
                                    </label>
                                </div>
                            </div>

                            <div className="module-modal-actions" style={{ display: 'flex', gap: '1rem' }}>
                                <button
                                    onClick={() => {
                                        setShowServiceModuleModal(false);
                                        setEditingModule(null);
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '6px',
                                        backgroundColor: 'white',
                                        color: '#374151',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleSaveModule}
                                    disabled={!moduleFormData.name || !moduleFormData.display_name}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem',
                                        border: 'none',
                                        borderRadius: '6px',
                                        backgroundColor: (!moduleFormData.name || !moduleFormData.display_name) ? '#d1d5db' : '#ef4444',
                                        color: 'white',
                                        cursor: (!moduleFormData.name || !moduleFormData.display_name) ? 'not-allowed' : 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    {editingModule ? 'Actualizar' : 'Crear'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Solicitud de Calificación */}
            {showReviewModal && (
                <div className="modal-overlay" onClick={() => setShowReviewModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px', padding: '0' }}>
                        <div className="modal-header" style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white',
                            padding: '1.5rem',
                            borderRadius: '16px 16px 0 0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div>
                                <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <MessageCircle size={28} />
                                    Calificación
                                </h2>
                                <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                                    Solicitar opinión al cliente
                                </p>
                            </div>
                            <button
                                onClick={() => setShowReviewModal(false)}
                                style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.5rem', cursor: 'pointer' }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ padding: '2rem' }}>
                            <p style={{ fontSize: '1.1rem', color: '#374151', marginBottom: '1.5rem', textAlign: 'center' }}>
                                ¿Deseas enviar un mensaje a <strong>{reviewClientInfo.name}</strong> para que nos califique en Google Maps?
                            </p>

                            <div style={{ background: '#f3f4f6', padding: '1rem', borderRadius: '12px', marginBottom: '2rem', fontSize: '0.9rem', color: '#4b5563', fontStyle: 'italic' }}>
                                "{whatsappReviewMessage}"
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <button
                                    onClick={() => setShowReviewModal(false)}
                                    style={{
                                        padding: '0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        background: 'white',
                                        color: '#374151',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    No por ahora
                                </button>
                                <button
                                    onClick={() => {
                                        openWhatsAppReview(reviewClientInfo.phone);
                                        setShowReviewModal(false);
                                    }}
                                    style={{
                                        padding: '0.75rem',
                                        border: 'none',
                                        borderRadius: '8px',
                                        background: '#25D366',
                                        color: 'white',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '0.5rem'
                                    }}
                                >
                                    <MessageCircle size={20} />
                                    Enviar WhatsApp
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <MultiAppointmentModal
                isOpen={showMultiAppointmentModal}
                onClose={() => {
                    setShowMultiAppointmentModal(false);
                    setSelectedClientForBooking(null);
                }}
                client={selectedClientForBooking}
                barbers={barbers}
                serviceModules={serviceModules}
                onSuccess={() => loadData()}
            />
        </div>
    );
};

export default AdminDashboard;
